import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import AdminCoachOutreach from "./AdminCoachOutreach";
import AdminDataHealth from "./AdminDataHealth";
import AdminScraperPanel from "./AdminScraperPanel";
import { toast } from "sonner";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip,
  AreaChart, Area, ResponsiveContainer,
} from "recharts";
import {
  Users, Mail, BookMarked, TrendingUp, RefreshCw, LogOut,
  Star, CircleDot, ArrowUpRight, ChevronUp, ChevronDown, ShieldCheck,
  Flag, Clock, CheckCircle2, AlertTriangle, XCircle, ChevronRight, Settings, Globe, Trash2,
  Download, Upload, Target, Zap, Activity, Send, Bell, UserCheck, School,
  Edit2, X, FileText, Plus, Building2, PenLine,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const adminReq = async (method, path, data) => {
  const token = localStorage.getItem("cb_admin_token");
  return axios({
    method,
    url: `${API}/api${path}`,
    data,
    headers: { Authorization: `Bearer ${token}` },
  });
};

const TIER_CONFIG = {
  free:       { label: "Free",        bg: "bg-slate-100 text-slate-600",    dot: "bg-slate-400",  bar: "#94a3b8" },
  trial:      { label: "Trial",       bg: "bg-blue-100 text-blue-700",      dot: "bg-blue-500",   bar: "#3b82f6" },
  basic:      { label: "Recruit",     bg: "bg-orange-100 text-orange-700",  dot: "bg-orange-500", bar: "#f97316" },
  premium:    { label: "Scholarship", bg: "bg-purple-100 text-purple-700",  dot: "bg-purple-500", bar: "#a855f7" },
  recruit:    { label: "Recruit",     bg: "bg-orange-100 text-orange-700",  dot: "bg-orange-500", bar: "#f97316" },
  scholarship:{ label: "Scholarship", bg: "bg-purple-100 text-purple-700",  dot: "bg-purple-500", bar: "#a855f7" },
};

function StatCard({ icon: Icon, label, value, sub, context, color = "text-orange-500" }) {
  return (
    <div data-testid={`stat-card-${label.replace(/\s+/g, "-").toLowerCase()}`}
      className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs font-bold uppercase tracking-wider text-slate-400">{label}</span>
        <Icon className={`w-4 h-4 ${color}`} />
      </div>
      <p className="text-3xl font-black text-slate-900" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{value ?? "—"}</p>
      {sub && <p className="text-xs text-slate-400 mt-1">{sub}</p>}
      {context && <p className="text-xs text-slate-300 mt-0.5">{context}</p>}
    </div>
  );
}

function TierBadge({ tier }) {
  const cfg = TIER_CONFIG[tier] || TIER_CONFIG.free;
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${cfg.bg}`}>
      <span className={`w-1.5 h-1.5 rounded-full ${cfg.dot}`} />
      {cfg.label}
    </span>
  );
}

function TierSelect({ userId, current, onChange }) {
  const [saving, setSaving] = useState(false);
  const handle = async (e) => {
    setSaving(true);
    try {
      await adminReq("patch", `/admin/users/${userId}/subscription`, { subscription_tier: e.target.value });
      onChange(userId, e.target.value);
    } catch {}
    setSaving(false);
  };
  return (
    <select
      data-testid={`tier-select-${userId}`}
      value={current}
      onChange={handle}
      disabled={saving}
      className="text-xs border border-slate-200 rounded-lg px-2 py-1.5 bg-white text-slate-700 font-semibold focus:ring-2 focus:ring-orange-500 outline-none disabled:opacity-60 cursor-pointer"
    >
      <option value="free">Free</option>
      <option value="trial">Trial</option>
      <option value="basic">Recruit</option>
      <option value="premium">Scholarship</option>
    </select>
  );
}

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleDateString("en-GB", { day: "2-digit", month: "short", year: "2-digit" }); }
  catch { return "—"; }
};

const relTime = (iso) => {
  if (!iso) return "Never";
  const diff = Date.now() - new Date(iso).getTime();
  const m = Math.floor(diff / 60000);
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  return d < 30 ? `${d}d ago` : fmtDate(iso);
};


function AdminPromoCodes() {
  const adminToken = localStorage.getItem("cb_admin_token");
  const adminReq = (method, path, data) =>
    axios({ method, url: `${API}/api${path}`, data, headers: { Authorization: `Bearer ${adminToken}` } });

  const [codes, setCodes]         = useState([]);
  const [loading, setLoading]     = useState(true);
  const [showForm, setShowForm]   = useState(false);
  const [deleting, setDeleting]   = useState(null);
  const [toggling, setToggling]   = useState(null);
  const BLANK_FORM = { code: "", code_type: "extension", extension_days: "30", discount_percent: "50", applicable_plan_type: "annual", max_uses: "", description: "", expires_at: "" };
  const [form, setForm]           = useState(BLANK_FORM);
  const [formError, setFormError] = useState("");
  const [creating, setCreating]   = useState(false);

  const load = async () => {
    try {
      const r = await adminReq("get", "/promo/admin/codes");
      setCodes(r.data || []);
    } catch {}
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (e) => {
    e.preventDefault();
    setFormError("");
    setCreating(true);
    try {
      const payload = {
        code: form.code,
        code_type: form.code_type,
        max_uses: form.max_uses ? parseInt(form.max_uses) : null,
        expires_at: form.expires_at || null,
        description: form.description,
      };
      if (form.code_type === "discount") {
        payload.discount_percent = parseFloat(form.discount_percent);
        payload.applicable_plan_type = form.applicable_plan_type;
      } else {
        payload.extension_days = parseInt(form.extension_days);
      }
      await adminReq("post", "/promo/admin/codes", payload);
      setForm(BLANK_FORM);
      setShowForm(false);
      await load();
    } catch (err) {
      setFormError(err?.response?.data?.detail || "Failed to create code");
    }
    setCreating(false);
  };

  const toggleActive = async (code, current) => {
    setToggling(code);
    try {
      await adminReq("patch", `/promo/admin/codes/${code}`, { is_active: !current });
      setCodes(prev => prev.map(c => c.code === code ? { ...c, is_active: !current } : c));
    } catch {}
    setToggling(null);
  };

  const deleteCode = async (code) => {
    if (!window.confirm(`Delete code "${code}"? This cannot be undone.`)) return;
    setDeleting(code);
    try {
      await adminReq("delete", `/promo/admin/codes/${code}`);
      setCodes(prev => prev.filter(c => c.code !== code));
    } catch {}
    setDeleting(null);
  };

  const DAYS_OPTIONS = [
    { value: "7",  label: "+7 days" },
    { value: "14", label: "+14 days" },
    { value: "30", label: "+30 days" },
    { value: "60", label: "+60 days" },
    { value: "90", label: "+90 days" },
  ];

  return (
    <div className="max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-black text-slate-900" style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
            Promo Codes
          </h2>
          <p className="text-xs text-slate-400 mt-0.5">Create time-extension codes or percentage discount codes for checkout.</p>
        </div>
        <button
          onClick={() => { setShowForm(s => !s); setFormError(""); }}
          className="bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2.5 rounded-xl text-sm transition-colors"
        >
          {showForm ? "Cancel" : "+ New Code"}
        </button>
      </div>

      {/* Create form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white border-2 border-orange-200 rounded-xl p-6 space-y-4">
          <h3 className="text-sm font-black text-slate-800 mb-2">New Promo Code</h3>
          {formError && <p className="text-xs text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{formError}</p>}

          {/* Code type toggle */}
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-2">Code Type *</label>
            <div className="flex gap-2">
              {[{ v: "extension", label: "Time Extension" }, { v: "discount", label: "% Discount at Checkout" }].map(t => (
                <button key={t.v} type="button"
                  onClick={() => setForm(f => ({ ...f, code_type: t.v }))}
                  className={`px-4 py-2 rounded-lg text-xs font-bold border transition-all ${form.code_type === t.v ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 text-slate-500 hover:border-slate-400"}`}>
                  {t.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Code *</label>
              <input
                data-testid="promo-code-input"
                value={form.code}
                onChange={e => setForm(f => ({ ...f, code: e.target.value.toUpperCase() }))}
                placeholder={form.code_type === "discount" ? "e.g. ANNUAL50" : "e.g. EXTEND30"}
                required
                className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm font-mono uppercase focus:outline-none focus:border-orange-400"
              />
            </div>

            {form.code_type === "extension" ? (
              <div>
                <label className="block text-xs font-bold text-slate-500 mb-1">Extension *</label>
                <select
                  data-testid="promo-days-select"
                  value={form.extension_days}
                  onChange={e => setForm(f => ({ ...f, extension_days: e.target.value }))}
                  className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                >
                  {DAYS_OPTIONS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
            ) : (
              <>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Discount % *</label>
                  <div className="relative">
                    <input type="number" min="1" max="100" step="1"
                      data-testid="promo-discount-input"
                      value={form.discount_percent}
                      onChange={e => setForm(f => ({ ...f, discount_percent: e.target.value }))}
                      placeholder="50"
                      required
                      className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 pr-8 text-sm focus:outline-none focus:border-orange-400"
                    />
                    <span className="absolute right-3 top-2.5 text-slate-400 text-sm font-bold">%</span>
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 mb-1">Applies To *</label>
                  <select
                    data-testid="promo-plan-type-select"
                    value={form.applicable_plan_type}
                    onChange={e => setForm(f => ({ ...f, applicable_plan_type: e.target.value }))}
                    className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
                  >
                    <option value="all">All plans</option>
                    <option value="annual">Annual plans only</option>
                    <option value="monthly">Monthly plans only</option>
                  </select>
                </div>
              </>
            )}

            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Max uses (blank = unlimited)</label>
              <input type="number" min="1" value={form.max_uses}
                onChange={e => setForm(f => ({ ...f, max_uses: e.target.value }))}
                placeholder="Unlimited"
                className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
              />
            </div>
            <div>
              <label className="block text-xs font-bold text-slate-500 mb-1">Expires on (optional)</label>
              <input type="date" value={form.expires_at}
                onChange={e => setForm(f => ({ ...f, expires_at: e.target.value }))}
                className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
              />
            </div>
          </div>
          <div>
            <label className="block text-xs font-bold text-slate-500 mb-1">Description (internal note)</label>
            <input value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              placeholder={form.code_type === "discount" ? "e.g. 50% off annual — summer campaign" : "e.g. Sent to athletes at London tournament"}
              className="w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:outline-none focus:border-orange-400"
            />
          </div>
          <button type="submit" disabled={creating} data-testid="promo-create-btn"
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-2.5 rounded-xl text-sm transition-colors disabled:opacity-60">
            {creating ? "Creating…" : "Create Code"}
          </button>
        </form>
      )}

      {/* Codes table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-sm text-slate-400">Loading…</div>
        ) : codes.length === 0 ? (
          <div className="p-8 text-center text-sm text-slate-400">No promo codes yet. Create your first one above.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                {["Code", "Type / Benefit", "Uses", "Expires", "Description", "Status", "Actions"].map(h => (
                  <th key={h} className="text-left text-xs font-bold text-slate-500 px-4 py-3">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {codes.map(c => (
                <tr key={c.code} className="hover:bg-slate-50">
                  <td className="px-4 py-3 font-mono font-bold text-slate-900 text-xs">{c.code}</td>
                  <td className="px-4 py-3">
                    {c.discount_percent != null ? (
                      <div className="flex flex-col gap-0.5">
                        <span className="bg-green-100 text-green-700 text-xs font-bold px-2 py-0.5 rounded-full w-fit">
                          {c.discount_percent}% off
                        </span>
                        <span className="text-slate-400 text-xs capitalize">{c.applicable_plan_type || "all"} plans</span>
                      </div>
                    ) : (
                      <span className="bg-orange-100 text-orange-700 text-xs font-bold px-2 py-0.5 rounded-full">+{c.extension_days} days</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-600 text-xs">{c.use_count}{c.max_uses ? ` / ${c.max_uses}` : " / ∞"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs">{c.expires_at ? new Date(c.expires_at).toLocaleDateString("en-GB") : "Never"}</td>
                  <td className="px-4 py-3 text-slate-500 text-xs max-w-[160px] truncate">{c.description || "—"}</td>
                  <td className="px-4 py-3">
                    <button data-testid={`toggle-${c.code}`}
                      onClick={() => toggleActive(c.code, c.is_active)} disabled={toggling === c.code}
                      className={`relative w-10 h-5 rounded-full transition-colors duration-200 focus:outline-none disabled:opacity-50 ${c.is_active ? "bg-emerald-500" : "bg-slate-300"}`}>
                      <span className={`absolute top-0.5 w-4 h-4 bg-white rounded-full shadow transition-transform duration-200 ${c.is_active ? "translate-x-5" : "translate-x-0.5"}`} />
                    </button>
                  </td>
                  <td className="px-4 py-3">
                    <button onClick={() => deleteCode(c.code)} disabled={deleting === c.code}
                      className="text-xs text-red-500 hover:text-red-700 font-bold transition-colors disabled:opacity-50">
                      {deleting === c.code ? "Deleting…" : "Delete"}
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}


function AdminLegalEditor() {
  const adminToken = localStorage.getItem("cb_admin_token");
  const [contents, setContents]   = useState({ privacy: "", terms: "" });
  const [activeDoc, setActiveDoc] = useState("privacy");
  const [loading, setLoading]     = useState(true);
  const [saving, setSaving]       = useState(false);
  const [saved, setSaved]         = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [p, t] = await Promise.all([
          axios.get(`${API}/api/legal/privacy`),
          axios.get(`${API}/api/legal/terms`),
        ]);
        setContents({ privacy: p.data.content || "", terms: t.data.content || "" });
      } catch {}
      setLoading(false);
    };
    load();
  }, [API]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(
        `${API}/api/legal/${activeDoc}`,
        { content: contents[activeDoc] },
        { headers: { Authorization: `Bearer ${adminToken}` } }
      );
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    } catch { alert("Save failed"); }
    setSaving(false);
  };

  return (
    <div className="max-w-4xl">
      <div className="bg-white border border-slate-200 rounded-xl p-6">
        <h2 className="text-xl font-black text-slate-900 mb-5" style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
          Legal Documents
        </h2>

        {/* Doc selector */}
        <div className="flex gap-2 mb-5">
          {[{ id: "privacy", label: "Privacy Policy" }, { id: "terms", label: "Terms of Use" }].map(d => (
            <button
              key={d.id}
              onClick={() => { setActiveDoc(d.id); setSaved(false); }}
              className={`px-4 py-2 rounded-lg text-xs font-bold transition-all ${activeDoc === d.id ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-500 hover:text-slate-700"}`}
            >
              {d.label}
            </button>
          ))}
          <a
            href={`/${activeDoc}`}
            target="_blank"
            rel="noreferrer"
            className="ml-auto px-4 py-2 rounded-lg text-xs font-bold bg-orange-50 text-orange-600 hover:bg-orange-100 transition-all"
          >
            Preview live page →
          </a>
        </div>

        {loading ? (
          <div className="h-64 bg-slate-100 rounded-xl animate-pulse" />
        ) : (
          <textarea
            value={contents[activeDoc]}
            onChange={e => setContents(c => ({ ...c, [activeDoc]: e.target.value }))}
            className="w-full h-[520px] border-2 border-slate-200 rounded-xl p-4 text-sm text-slate-800 font-mono leading-relaxed resize-none focus:outline-none focus:border-orange-400 transition-colors"
            spellCheck={false}
          />
        )}

        <div className="flex items-center gap-4 mt-4">
          <button
            onClick={handleSave}
            disabled={saving || loading}
            className="bg-slate-900 hover:bg-slate-800 text-white font-bold px-6 py-2.5 rounded-xl text-sm transition-colors disabled:opacity-60 flex items-center gap-2"
          >
            {saving ? "Saving…" : "Save Changes"}
          </button>
          {saved && <span className="text-xs text-green-600 font-bold">✓ Saved successfully</span>}
          <span className="text-xs text-slate-400 ml-auto">
            Use **text** for bold, * items for bullet lists. Full-line **Heading** becomes a section header.
          </span>
        </div>
      </div>
    </div>
  );
}


export default function AdminPage() {
  const navigate  = useNavigate();
  const [stats, setStats]     = useState(null);
  const [users, setUsers]     = useState([]);
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState("overview");
  const [sort, setSort]       = useState({ key: "created_at", dir: "desc" });
  const [search, setSearch]   = useState("");
  const [refreshedAt, setRefreshedAt] = useState(null);
  const [resolvingId, setResolvingId] = useState(null);
  const [resolveForm, setResolveForm] = useState({ status: "fixed", message: "" });
  const [savingSettings, setSavingSettings] = useState(false);
  const [settingsSaved, setSettingsSaved] = useState(false);
  const [appSettings, setAppSettings] = useState({ january_enrollment_enabled: true });
  const [appSettingsLoaded, setAppSettingsLoaded] = useState(false);
  // Coach portal
  const [coaches, setCoaches] = useState([]);
  const [coachesLoaded, setCoachesLoaded] = useState(false);
  const [coachFilter, setCoachFilter] = useState("all");
  const [verifyingCoach, setVerifyingCoach] = useState(null);
  const [verifyForm, setVerifyForm] = useState({ notes: "" });
  // Pricing management
  const [pricingPlans, setPricingPlans] = useState([]);
  const [editingPlan, setEditingPlan] = useState(null);
  const [pricingForm, setPricingForm] = useState({});
  const [pricingSaving, setPricingSaving] = useState(false);
  const [pricingSaved, setPricingSaved] = useState(null);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [fixingEmail, setFixingEmail] = useState(null);
  const [emailFixValue, setEmailFixValue] = useState("");
  const [emailFixSaving, setEmailFixSaving] = useState(false);
  const [emailFixDone, setEmailFixDone] = useState(null);
  // Analytics tabs
  const [funnel, setFunnel]     = useState(null);
  const [outreach, setOutreach] = useState(null);
  const [activity, setActivity] = useState(null);
  const [activityFilter, setActivityFilter] = useState("all");
  const [selectedUsers, setSelectedUsers] = useState(new Set());
  const [nudging, setNudging]   = useState(false);
  const [nudgeResult, setNudgeResult] = useState(null);
  // Colleges contacts tab
  const [contacts, setContacts] = useState([]);
  const [contactsLoaded, setContactsLoaded] = useState(false);
  const [contactSearch, setContactSearch] = useState("");
  const [contactFilter, setContactFilter] = useState("all");
  const [inlineEdit, setInlineEdit] = useState(null);
  const [contactGender, setContactGender] = useState("all");
  const [inlineValue, setInlineValue] = useState("");
  const [inlineNameValue, setInlineNameValue] = useState("");
  const [inlineLvValue, setInlineLvValue] = useState("");
  const [inlineSaving, setInlineSaving] = useState(false);
  const [inlineDone, setInlineDone] = useState({});
  const [inlineSaveError, setInlineSaveError] = useState("");
  const [importResult, setImportResult] = useState(null);
  const [importing, setImporting] = useState(false);
  // College edit modal
  const [editCollege, setEditCollege] = useState(null); // full college object
  const [editForm, setEditForm] = useState({});
  const [editSaving, setEditSaving] = useState(false);
  const [editSaved, setEditSaved] = useState(false);
  // Per-gender coach section save state
  const [editSavingMens, setEditSavingMens] = useState(false);
  const [editSavedMens, setEditSavedMens] = useState(false);
  const [editSavingWomens, setEditSavingWomens] = useState(false);
  const [editSavedWomens, setEditSavedWomens] = useState(false);
  // Per-gender late opening state (replaces old single editForm.late_opening_*)
  const [lateOpeningMens, setLateOpeningMens] = useState({ status: "unconfirmed", confidence: "", source: "", notes: "" });
  const [lateOpeningWomens, setLateOpeningWomens] = useState({ status: "unconfirmed", confidence: "", source: "", notes: "" });
  const [lateOpeningMensVerified, setLateOpeningMensVerified] = useState(null);
  const [lateOpeningWomensVerified, setLateOpeningWomensVerified] = useState(null);
  const [editSavingLateOpeningMens, setEditSavingLateOpeningMens] = useState(false);
  const [editSavedLateOpeningMens, setEditSavedLateOpeningMens] = useState(false);
  const [editSavingLateOpeningWomens, setEditSavingLateOpeningWomens] = useState(false);
  const [editSavedLateOpeningWomens, setEditSavedLateOpeningWomens] = useState(false);
  // Bulk college import
  const [bulkImportResult, setBulkImportResult] = useState(null);
  const [bulkImporting, setBulkImporting] = useState(false);
  const [pendingColleges, setPendingColleges] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [approvingId, setApprovingId] = useState(null);
  const adminEmail = localStorage.getItem("cb_admin_email") || "Admin";
  const [pendingCoachCount, setPendingCoachCount] = useState(0);
  // Growth Tracker
  const [outreachSubTab, setOutreachSubTab] = useState("analytics");
  const [growthTargets, setGrowthTargets] = useState([]);
  const [growthTargetsLoaded, setGrowthTargetsLoaded] = useState(false);
  const [growthContentBank, setGrowthContentBank] = useState([]);
  const [contentBankLoaded, setContentBankLoaded] = useState(false);
  const [editingTarget, setEditingTarget] = useState(null);
  const [targetForm, setTargetForm] = useState({});
  const [targetFormSaving, setTargetFormSaving] = useState(false);
  const [showNewTarget, setShowNewTarget] = useState(false);
  const [newTargetForm, setNewTargetForm] = useState({ name: "", region: "", tier: "tier_3", conference: "", notes: "", contact_name: "", contact_email: "", contact_role: "" });
  const [creatingTarget, setCreatingTarget] = useState(false);
  const [approvingTarget, setApprovingTarget] = useState(null);
  const [sendingTarget, setSendingTarget] = useState(null);
  const [showNewContent, setShowNewContent] = useState(false);
  const [newContentForm, setNewContentForm] = useState({ pillar: "", title: "", body: "", status: "ready" });
  const [creatingContent, setCreatingContent] = useState(false);
  const [editingContent, setEditingContent] = useState(null);
  const [contentForm, setContentForm] = useState({});
  const [contentFormSaving, setContentFormSaving] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [sRes, uRes, rRes, pRes] = await Promise.all([
        adminReq("get", "/admin/stats"),
        adminReq("get", "/admin/users"),
        adminReq("get", "/admin/reports"),
        adminReq("get", "/admin/pricing"),
      ]);
      setStats(sRes.data);
      setUsers(uRes.data);
      setReports(rRes.data);
      setPricingPlans(pRes.data || []);
      setRefreshedAt(new Date());
    } catch (e) {
      if (e?.response?.status === 401) {
        localStorage.removeItem("cb_admin_token");
        navigate("/admin/login", { replace: true });
      }
    }
    // Fetch pending coach count for badge
    try {
      const qRes = await adminReq("get", "/coach/admin/queue");
      setPendingCoachCount(qRes.data?.stats?.total_pending || 0);
    } catch {}
    setLoading(false);
  }, [navigate]);

  const loadFunnel   = useCallback(async () => { try { const r = await adminReq("get", "/admin/funnel");   setFunnel(r.data);   } catch {} }, []);
  const loadOutreach = useCallback(async () => { try { const r = await adminReq("get", "/admin/outreach"); setOutreach(r.data); } catch {} }, []);
  const loadActivity = useCallback(async () => { try { const r = await adminReq("get", "/admin/activity"); setActivity(r.data); } catch {} }, []);
  const loadCoaches  = useCallback(async () => { try { const r = await adminReq("get", "/admin/coaches"); setCoaches(r.data || []); setCoachesLoaded(true); } catch {} }, []);
  const loadGrowthTargets = useCallback(async () => { try { const r = await adminReq("get", "/admin/growth/academy-targets"); setGrowthTargets(r.data || []); setGrowthTargetsLoaded(true); } catch {} }, []);
  const loadContentBank   = useCallback(async () => { try { const r = await adminReq("get", "/admin/growth/content-bank");   setGrowthContentBank(r.data || []); setContentBankLoaded(true);   } catch {} }, []);
  const loadAppSettings   = useCallback(async () => {
    try {
      const r = await adminReq("get", "/admin/settings/january-enrollment");
      setAppSettings(prev => ({ ...prev, january_enrollment_enabled: r.data.enabled }));
      setAppSettingsLoaded(true);
    } catch {}
  }, []);

  const sendNudge = async (userIds) => {
    setNudging(true); setNudgeResult(null);
    try {
      const r = await adminReq("post", "/admin/nudge", { user_ids: userIds });
      setNudgeResult(`Sent ${r.data.sent} nudge email${r.data.sent !== 1 ? "s" : ""} successfully`);
      setSelectedUsers(new Set());
    } catch { setNudgeResult("Failed to send — check Resend config"); }
    setNudging(false);
  };

  // ── Growth Tracker actions ────────────────────────────────────────────────
  const openEditTarget = (tgt) => {
    setEditingTarget(tgt);
    setTargetForm({
      contact_name: tgt.contact_name || "",
      contact_email: tgt.contact_email || "",
      contact_role: tgt.contact_role || "",
      draft_email_subject: tgt.draft_email_subject || "",
      draft_email_body: tgt.draft_email_body || "",
      notes: tgt.notes || "",
    });
  };

  const saveTargetForm = async () => {
    if (!editingTarget) return;
    setTargetFormSaving(true);
    try {
      const payload = {};
      Object.entries(targetForm).forEach(([k, v]) => { if (v !== undefined) payload[k] = v || null; });
      const r = await adminReq("patch", `/admin/growth/academy-targets/${editingTarget.id}`, payload);
      setGrowthTargets(prev => prev.map(t => t.id === editingTarget.id ? r.data : t));
      setEditingTarget(r.data);
      toast.success("Target saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setTargetFormSaving(false);
  };

  const approveTarget = async (tgtId) => {
    setApprovingTarget(tgtId);
    try {
      const r = await adminReq("post", `/admin/growth/academy-targets/${tgtId}/approve`);
      setGrowthTargets(prev => prev.map(t => t.id === tgtId ? r.data : t));
      if (editingTarget?.id === tgtId) setEditingTarget(r.data);
      toast.success("Draft approved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Approval failed");
    }
    setApprovingTarget(null);
  };

  const sendTargetEmail = async (tgtId) => {
    setSendingTarget(tgtId);
    try {
      const r = await adminReq("post", `/admin/growth/academy-targets/${tgtId}/send`);
      setGrowthTargets(prev => prev.map(t => t.id === tgtId ? (r.data.target || { ...t, send_status: "sent" }) : t));
      if (editingTarget?.id === tgtId) setEditingTarget(prev => ({ ...prev, send_status: "sent" }));
      toast.success(`Email sent to ${r.data.to}`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Send failed");
    }
    setSendingTarget(null);
  };

  const createTarget = async () => {
    if (!newTargetForm.name.trim()) { toast.error("Academy name is required"); return; }
    setCreatingTarget(true);
    try {
      const r = await adminReq("post", "/admin/growth/academy-targets", newTargetForm);
      setGrowthTargets(prev => [r.data, ...prev]);
      setShowNewTarget(false);
      setNewTargetForm({ name: "", region: "", tier: "tier_3", conference: "", notes: "", contact_name: "", contact_email: "", contact_role: "" });
      toast.success("Target created");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
    setCreatingTarget(false);
  };

  const createContentItem = async () => {
    if (!newContentForm.title.trim() || !newContentForm.body.trim()) { toast.error("Title and body are required"); return; }
    setCreatingContent(true);
    try {
      const r = await adminReq("post", "/admin/growth/content-bank", newContentForm);
      setGrowthContentBank(prev => [r.data, ...prev]);
      setShowNewContent(false);
      setNewContentForm({ pillar: "", title: "", body: "", status: "draft" });
      toast.success("Content draft saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    }
    setCreatingContent(false);
  };

  const saveContentItem = async () => {
    if (!editingContent) return;
    setContentFormSaving(true);
    try {
      const r = await adminReq("patch", `/admin/growth/content-bank/${editingContent.id}`, contentForm);
      setGrowthContentBank(prev => prev.map(c => c.id === editingContent.id ? r.data : c));
      setEditingContent(null);
      toast.success("Content saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    }
    setContentFormSaving(false);
  };

  useEffect(() => {
    if (!localStorage.getItem("cb_admin_token")) {
      navigate("/admin/login", { replace: true });
      return;
    }
    load();
  }, [load, navigate]);

  const logout = async () => {
    try { await adminReq("post", "/admin/logout"); } catch {}
    localStorage.removeItem("cb_admin_token");
    localStorage.removeItem("cb_admin_email");
    navigate("/admin/login", { replace: true });
  };

  const updateTier = (userId, tier) =>
    setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, subscription_tier: tier } : u));

  const savePricing = async (tier) => {
    setPricingSaving(true);
    try {
      const featuresArr = typeof pricingForm.features === "string"
        ? pricingForm.features.split("\n").map(f => f.trim()).filter(Boolean)
        : pricingForm.features;
      await adminReq("put", `/admin/pricing/${tier}`, {
        ...pricingForm,
        price_monthly: parseFloat(pricingForm.price_monthly),
        price_annual: pricingForm.price_annual !== "" ? parseFloat(pricingForm.price_annual) : null,
        features: featuresArr,
      });
      setPricingPlans(prev => prev.map(p => p.tier === tier ? { ...p, ...pricingForm, features: featuresArr } : p));
      setPricingSaved(tier);
      setEditingPlan(null);
      setTimeout(() => setPricingSaved(null), 3000);
    } catch {}
    setPricingSaving(false);
  };

  const deleteUser = async () => {
    if (!confirmDelete) return;
    setDeleting(true);
    try {
      await adminReq("delete", `/admin/users/${confirmDelete.user_id}`);
      setUsers(prev => prev.filter(u => u.user_id !== confirmDelete.user_id));
      setConfirmDelete(null);
    } catch {}
    setDeleting(false);
  };

  const toggleDisableUser = async (userId, currentlyDisabled) => {
    try {
      await adminReq("patch", `/admin/users/${userId}/disable`, { disabled: !currentlyDisabled });
      setUsers(prev => prev.map(u => u.user_id === userId ? { ...u, is_disabled: !currentlyDisabled } : u));
    } catch (e) {
      alert(e?.response?.data?.detail || "Run migration first: ALTER TABLE users ADD COLUMN IF NOT EXISTS is_disabled BOOLEAN DEFAULT FALSE;");
    }
  };

  const exportContacts = async (filterType) => {
    const token = localStorage.getItem("cb_admin_token");
    const API = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${API}/api/admin/colleges-contacts/export?filter=${filterType}`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `courtbound_contacts_${filterType}_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setImporting(true);
    setImportResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const token = localStorage.getItem("cb_admin_token");
      const API = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API}/api/admin/colleges-contacts/import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      setImportResult(data);
      if (data.updated > 0) {
        // Refresh the contacts list
        setContactsLoaded(false);
        await loadContacts();
      }
    } catch {
      setImportResult({ ok: false, error: "Upload failed. Check file format." });
    }
    setImporting(false);
    e.target.value = "";
  };

  const [confirmDeleteCoach, setConfirmDeleteCoach] = useState(null);
  const [deletingCoach, setDeletingCoach] = useState(false);

  const deleteCoach = async () => {
    if (!confirmDeleteCoach) return;
    setDeletingCoach(true);
    try {
      await adminReq("delete", `/admin/colleges/${confirmDeleteCoach.college_id}/coaches`, {
        coach_name: confirmDeleteCoach.coach_name,
        programme_gender: confirmDeleteCoach.programme_gender || null,
      });
      setContacts(prev => prev.filter(c => {
        const sameCollege = c.college_id === confirmDeleteCoach.college_id;
        const sameName    = c.coach_name === confirmDeleteCoach.coach_name;
        const sameGender  = confirmDeleteCoach.programme_gender
          ? c.programme_gender === confirmDeleteCoach.programme_gender
          : true;
        return !(sameCollege && sameName && sameGender);
      }));
      setConfirmDeleteCoach(null);
    } catch {}
    setDeletingCoach(false);
  };

  const openEditCollege = async (college_id) => {
    try {
      const { data } = await adminReq("get", `/admin/colleges/${college_id}`);
      setEditCollege(data);
      const allCoaches = data.coaches || [];
      // Per-gender late opening — read from the new late_openings_by_gender map
      const loMens   = data.late_openings_by_gender?.mens   || {};
      const loWomens = data.late_openings_by_gender?.womens || {};
      setLateOpeningMens({
        status:     loMens.status     || "unconfirmed",
        confidence: loMens.confidence || "",
        source:     loMens.source     || "",
        notes:      loMens.notes      || "",
      });
      setLateOpeningMensVerified(
        loMens.verified_at ? { verified_by: loMens.verified_by, verified_at: loMens.verified_at } : null
      );
      setLateOpeningWomens({
        status:     loWomens.status     || "unconfirmed",
        confidence: loWomens.confidence || "",
        source:     loWomens.source     || "",
        notes:      loWomens.notes      || "",
      });
      setLateOpeningWomensVerified(
        loWomens.verified_at ? { verified_by: loWomens.verified_by, verified_at: loWomens.verified_at } : null
      );
      setEditForm({
        name: data.name || "",
        location: data.location || "",
        state: data.state || "",
        division: data.division || "",
        conference: data.conference || "",
        region: data.region || "USA",
        foreign_friendly: data.foreign_friendly ? "yes" : "no",
        scholarship_info: data.scholarship_info || "",
        acceptance_rate: data.acceptance_rate || "",
        notable_alumni: data.notable_alumni || "",
        ranking: data.ranking ?? "",
        website: data.website || "",
        image_url: data.image_url || "",
        // Split coaches into gender sections — never mixed in a single list again
        mensCoaches:       JSON.parse(JSON.stringify(allCoaches.filter(c => c.programme_gender === "mens"))),
        womensCoaches:     JSON.parse(JSON.stringify(allCoaches.filter(c => c.programme_gender === "womens"))),
        unassignedCoaches: JSON.parse(JSON.stringify(allCoaches.filter(c => c.programme_gender !== "mens" && c.programme_gender !== "womens"))),
      });
    } catch (err) {
      console.error("openEditCollege failed:", err);
    }
  };

  const saveEditCollege = async () => {
    if (!editCollege) return;
    setEditSaving(true);
    try {
      // Exclude coach arrays — they are saved independently via saveCoachSection()
      const { mensCoaches, womensCoaches, unassignedCoaches, ...formFields } = editForm;
      const payload = {
        ...formFields,
        foreign_friendly: formFields.foreign_friendly === "yes",
        ranking: formFields.ranking !== "" ? Number(formFields.ranking) : null,
      };
      await adminReq("patch", `/admin/colleges/${editCollege.id}/details`, payload);
      await loadContacts(true);
      setEditSaved(true);
      setTimeout(() => { setEditSaved(false); setEditCollege(null); }, 1500);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string"
        ? detail
        : Array.isArray(detail)
          ? detail.map(d => d.msg || JSON.stringify(d)).join("; ")
          : "Save failed. Please try again.";
      toast.error(msg);
    }
    setEditSaving(false);
  };

  const saveCoachSection = async (gender) => {
    if (!editCollege) return;
    const coaches = gender === "mens" ? editForm.mensCoaches : editForm.womensCoaches;
    const setSaving = gender === "mens" ? setEditSavingMens : setEditSavingWomens;
    const setSaved  = gender === "mens" ? setEditSavedMens  : setEditSavedWomens;
    setSaving(true);
    try {
      await adminReq("patch", `/admin/colleges/${editCollege.id}/details`, {
        coaches,
        coach_programme_gender: gender,
      });
      await loadContacts(true);
      toast.success(`${gender === "mens" ? "Men's" : "Women's"} coaches saved.`);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed.");
    }
    setSaving(false);
  };

  const saveLateOpeningForGender = async (gender) => {
    if (!editCollege) return;
    const form       = gender === "mens" ? lateOpeningMens   : lateOpeningWomens;
    const setSaving  = gender === "mens" ? setEditSavingLateOpeningMens  : setEditSavingLateOpeningWomens;
    const setSaved   = gender === "mens" ? setEditSavedLateOpeningMens   : setEditSavedLateOpeningWomens;
    const setVerified = gender === "mens" ? setLateOpeningMensVerified    : setLateOpeningWomensVerified;
    setSaving(true);
    try {
      const payload = {
        programme_gender:        gender,
        late_opening_status:     form.status     || "unconfirmed",
        late_opening_confidence: form.confidence || null,
        late_opening_source:     form.source     || null,
        late_opening_notes:      form.notes      || null,
      };
      const { data } = await adminReq("patch", `/admin/colleges/${editCollege.id}/late-opening`, payload);
      setVerified(data.verified_at ? { verified_by: data.verified_by, verified_at: data.verified_at } : null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
      toast.success(`${gender === "mens" ? "Men's" : "Women's"} late opening saved.`);
    } catch (err) {
      const detail = err?.response?.data?.detail;
      toast.error(typeof detail === "string" ? detail : "Save failed — check status/confidence values.");
    }
    setSaving(false);
  };

  const handleBulkCollegeImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBulkImporting(true);
    setBulkImportResult(null);
    const formData = new FormData();
    formData.append("file", file);
    try {
      const token = localStorage.getItem("cb_admin_token");
      const res = await fetch(`${API}/api/admin/colleges/bulk-import`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      const data = await res.json();
      setBulkImportResult(data);
      if (data.inserted > 0) loadPendingColleges();
    } catch {
      setBulkImportResult({ error: "Upload failed. Check file format." });
    }
    setBulkImporting(false);
    e.target.value = "";
  };

  const loadPendingColleges = async () => {
    setPendingLoading(true);
    try {
      const r = await adminReq("get", "/admin/colleges/pending");
      setPendingColleges(r.data || []);
    } catch {}
    setPendingLoading(false);
  };

  const approveCollege = async (id) => {
    setApprovingId(id);
    try {
      await adminReq("post", `/admin/colleges/${id}/approve`);
      setPendingColleges(prev => prev.filter(c => c.id !== id));
      toast.success("College approved and now live!");
    } catch {
      toast.error("Failed to approve college");
    }
    setApprovingId(null);
  };

  const rejectCollege = async (id, name) => {
    if (!window.confirm(`Delete "${name}"? This cannot be undone.`)) return;
    try {
      await adminReq("delete", `/admin/colleges/${id}`);
      setPendingColleges(prev => prev.filter(c => c.id !== id));
      toast.success(`${name} removed.`);
    } catch {
      toast.error("Failed to delete college");
    }
  };

  const exportBulkColleges = async () => {
    const token = localStorage.getItem("cb_admin_token");
    const API = process.env.REACT_APP_BACKEND_URL;
    const res = await fetch(`${API}/api/admin/colleges/bulk-export`, {
      headers: { Authorization: `Bearer ${token}` },
    });
    const blob = await res.blob();
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `courtbound_colleges_${new Date().toISOString().slice(0,10)}.csv`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadContacts = async (forceRefresh = false) => {
    if (contactsLoaded && !forceRefresh) return;
    try {
      const res = await adminReq("get", "/admin/colleges-contacts");
      setContacts(res.data);
      setContactsLoaded(true);
    } catch {
      // Transient backend error — do not propagate to React error boundary
      toast.error("Could not load contacts. Please try again.");
    }
  };

  const saveInlineEmail = async () => {
    if (!inlineEdit) return;
    if (!inlineValue.trim() && !inlineNameValue.trim() && !inlineLvValue.trim()) return;
    setInlineSaving(true);
    setInlineSaveError("");
    try {
      await adminReq("patch", `/admin/colleges/${inlineEdit.college_id}/coach-email`, {
        coach_id:         inlineEdit.coach_id,
        old_coach_name:   inlineEdit.coach_name,
        new_coach_name:   inlineNameValue.trim() || undefined,
        new_coach_email:  inlineValue.trim() || undefined,
        last_verified:    inlineLvValue.trim() || undefined,
        programme_gender: inlineEdit.programme_gender || undefined,
      });
      // Force re-fetch so UI always reflects actual DB state
      try {
        await loadContacts(true);
      } catch {
        // Re-fetch failed — close form anyway but warn user
        toast.warning("Saved, but could not refresh the list. Please reload the page.");
      }
      setInlineEdit(null);
      setInlineValue("");
      setInlineNameValue("");
      setInlineLvValue("");
      toast.success("Coach details saved.");
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = typeof detail === "string" ? detail : "Save failed. Please try again.";
      setInlineSaveError(msg);
      toast.error(msg);
    }
    setInlineSaving(false);
  };

  const applyEmailFix = async (r) => {    if (!emailFixValue.trim()) return;
    setEmailFixSaving(true);
    try {
      await adminReq("patch", `/admin/colleges/${r.college_id}/coach-email`, {
        old_coach_name: r.coach_name,
        new_coach_email: emailFixValue.trim(),
      });
      // also auto-mark the report as fixed
      await adminReq("patch", `/admin/reports/${r.id}`, {
        status: "fixed",
        admin_response: `Email updated to ${emailFixValue.trim()}. Bounced emails removed from user history.`,
      });
      setReports(prev => prev.map(rep => rep.id === r.id
        ? { ...rep, status: "fixed", admin_response: `Email updated to ${emailFixValue.trim()}. Bounced emails removed from user history.` }
        : rep
      ));
      setEmailFixDone(r.id);
      setFixingEmail(null);
      setEmailFixValue("");
    } catch {}
    setEmailFixSaving(false);
  };

  const toggleSort = (key) =>
    setSort(s => s.key === key ? { key, dir: s.dir === "asc" ? "desc" : "asc" } : { key, dir: "desc" });

  const SortIcon = ({ k }) => {
    if (sort.key !== k) return <ChevronUp className="w-3 h-3 opacity-20" />;
    return sort.dir === "asc" ? <ChevronUp className="w-3 h-3 text-orange-500" /> : <ChevronDown className="w-3 h-3 text-orange-500" />;
  };

  const filteredUsers = users
    .filter(u => {
      const q = search.toLowerCase();
      return !q || u.name?.toLowerCase().includes(q) || u.email?.toLowerCase().includes(q);
    })
    .sort((a, b) => {
      const av = a[sort.key] ?? ""; const bv = b[sort.key] ?? "";
      const cmp = typeof av === "number" ? av - bv : String(av).localeCompare(String(bv));
      return sort.dir === "asc" ? cmp : -cmp;
    });

  const subData = stats && stats.subscription_breakdown
    ? Object.entries(stats.subscription_breakdown).map(([name, value]) => ({
        name: TIER_CONFIG[name]?.label || name, value,
        color: TIER_CONFIG[name]?.bar || "#94a3b8",
      }))
    : [];

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-10 w-10 border-4 border-orange-500 border-t-transparent" />
      </div>
    );
  }

  return (
    <div style={{ fontFamily: "Manrope, sans-serif" }} className="min-h-screen bg-slate-50 p-6">

      {/* Header */}
      <div className="flex items-center justify-between mb-7 flex-wrap gap-3">
        <div>
          <div className="flex items-center gap-2 mb-0.5">
            <ShieldCheck className="w-4 h-4 text-orange-500" />
            <span className="text-xs tracking-[0.2em] uppercase font-bold text-orange-600">Admin Dashboard</span>
          </div>
          <h1 className="text-4xl font-black uppercase text-slate-900" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
            CourtBound
          </h1>
          <p className="text-xs text-slate-400 mt-0.5">
            Signed in as <span className="font-semibold text-slate-600">{adminEmail}</span>
            {refreshedAt && <> · Updated {relTime(refreshedAt.toISOString())}</>}
          </p>
        </div>
        <div className="flex gap-2">
          <button
            data-testid="admin-coach-verification-btn"
            onClick={() => navigate("/admin/coach-verification")}
            className="relative flex items-center gap-2 bg-blue-50 border border-blue-200 text-blue-700 hover:bg-blue-100 font-bold text-sm px-4 py-2.5 rounded-xl transition-all">
            <UserCheck className="w-4 h-4" /> Coach Verification
            {pendingCoachCount > 0 && (
              <span className="absolute -top-2 -right-2 bg-red-500 text-white text-xs font-black rounded-full min-w-5 h-5 flex items-center justify-center px-1 leading-none">
                {pendingCoachCount}
              </span>
            )}
          </button>
          <button data-testid="admin-refresh-btn" onClick={load}
            className="flex items-center gap-2 bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-bold text-sm px-4 py-2.5 rounded-xl transition-all">
            <RefreshCw className="w-4 h-4" /> Refresh
          </button>
          <button data-testid="admin-logout-btn" onClick={logout}
            className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-600 hover:bg-red-100 font-bold text-sm px-4 py-2.5 rounded-xl transition-all">
            <LogOut className="w-4 h-4" /> Logout
          </button>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-6 bg-white border border-slate-200 rounded-xl p-1 w-fit flex-wrap">
        {[
          { id: "overview",       label: "Overview" },
          { id: "users",          label: `Users (${users.length})` },
          { id: "coaches",        label: "Coaches" },
          { id: "coach-outreach", label: "Coach Outreach" },
          { id: "funnel",         label: "Funnel" },
          { id: "outreach",       label: "Outreach" },
          { id: "activity",  label: "Activity" },
          { id: "reports",   label: `Reports${reports.filter(r => r.status === "pending").length > 0 ? ` (${reports.filter(r => r.status === "pending").length})` : ""}` },
          { id: "colleges",  label: "Colleges" },
          { id: "pricing",   label: "Pricing" },
          { id: "promos",    label: "Promo Codes" },
          { id: "legal",     label: "Legal" },
          { id: "settings",  label: "Settings" },
          { id: "data-health", label: "Data Health" },
          { id: "scraper",     label: "Scraper"      },
        ].map(t => (
          <button key={t.id} onClick={() => {
            setActiveTab(t.id);
            if (t.id === "colleges") loadPendingColleges();
            if (t.id === "colleges") loadContacts();
            if (t.id === "funnel"   && !funnel)   loadFunnel();
            if (t.id === "outreach" && !outreach) loadOutreach();
            if (t.id === "activity" && !activity) loadActivity();            if (t.id === "coaches") loadCoaches();
            if (t.id === "settings" && !appSettingsLoaded) loadAppSettings();
          }}
            className={`px-4 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${activeTab === t.id ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {/* ── OVERVIEW TAB ─────────────────────────────────────── */}
      {activeTab === "overview" && (<>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={Users}      label="Total Users"      value={stats?.users?.total}       sub={`+${stats?.users?.new_7d ?? 0} this week`} />
        <StatCard icon={TrendingUp} label="Active (7 days)"  value={stats?.users?.active_7d}   sub={`${stats?.users?.active_30d ?? 0} active last 30d`} color="text-blue-500" />
        <StatCard icon={Mail}       label="Emails Sent"      value={stats?.emails?.total_sent} sub={`+${stats?.emails?.sent_7d ?? 0} this week`} color="text-green-500" />
        <StatCard icon={BookMarked} label="Colleges Tracked" value={stats?.colleges_tracked}   sub="across all users" color="text-purple-500" />
      </div>

      {/* College coverage stats */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <StatCard icon={School} label="Men's Coverage"   value={stats?.college_stats?.mens?.live}   sub={`colleges with ≥1 contactable men's coach`}   context={`out of ${stats?.total_colleges ?? "—"} colleges · ${stats?.coach_totals?.mens ?? "—"} coaches total`}   color="text-blue-500" />
        <StatCard icon={School} label="Women's Coverage" value={stats?.college_stats?.womens?.live} sub={`colleges with ≥1 contactable women's coach`} context={`out of ${stats?.total_colleges ?? "—"} colleges · ${stats?.coach_totals?.womens ?? "—"} coaches total`} color="text-pink-500" />
        <StatCard icon={ShieldCheck} label="Verified — Men's"   value={stats?.college_stats?.mens?.verified}   sub="admin-verified coaches" color="text-blue-500" />
        <StatCard icon={ShieldCheck} label="Verified — Women's" value={stats?.college_stats?.womens?.verified} sub="admin-verified coaches" color="text-pink-500" />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-6">
        {/* Signup Trend */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">New Signups — Last 30 Days</p>
          {stats?.signup_trend?.length > 0 ? (
            <ResponsiveContainer width="100%" height={180}>
              <LineChart data={stats.signup_trend} margin={{ top: 2, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
                <Tooltip contentStyle={{ background: "#1e293b", border: "none", borderRadius: 8, fontSize: 12 }}
                  labelStyle={{ color: "#94a3b8" }} itemStyle={{ color: "#f97316" }} />
                <Line type="monotone" dataKey="signups" stroke="#f97316" strokeWidth={2} dot={{ r: 3, fill: "#f97316" }} />
              </LineChart>
            </ResponsiveContainer>
          ) : (
            <div className="h-44 flex items-center justify-center text-slate-400 text-sm">No signup data for the last 30 days</div>
          )}
        </div>

        {/* Subscription Breakdown */}
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Subscription Breakdown</p>
          <div className="space-y-4 mt-2">
            {subData.length > 0 ? subData.map(d => {
              const total = subData.reduce((s, x) => s + x.value, 0);
              const pct = total > 0 ? Math.round((d.value / total) * 100) : 0;
              return (
                <div key={d.name}>
                  <div className="flex justify-between mb-1">
                    <span className="text-sm font-bold text-slate-700">{d.name}</span>
                    <span className="text-sm font-black text-slate-900">{d.value} <span className="text-xs text-slate-400 font-normal">({pct}%)</span></span>
                  </div>
                  <div className="h-2 bg-slate-100 rounded-full overflow-hidden">
                    <div className="h-full rounded-full" style={{ width: `${pct}%`, background: d.color }} />
                  </div>
                </div>
              );
            }) : <p className="text-slate-400 text-sm text-center pt-8">No data yet</p>}
          </div>
          <div className="mt-5 pt-4 border-t border-slate-100 flex items-center justify-between">
            <span className="text-xs text-slate-400 font-semibold">Total Users</span>
            <span className="text-xl font-black text-slate-900" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{stats?.users?.total ?? 0}</span>
          </div>
        </div>
      </div>

      {/* Email Activity */}
      <div className="bg-white border border-slate-200 rounded-xl p-5 mb-6">
        <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Emails Sent — Last 14 Days</p>
        {stats?.email_trend?.length > 0 ? (
          <ResponsiveContainer width="100%" height={160}>
            <BarChart data={stats.email_trend} margin={{ top: 2, right: 8, left: -20, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
              <XAxis dataKey="date" tick={{ fontSize: 10, fill: "#94a3b8" }} tickFormatter={d => d.slice(5)} />
              <YAxis tick={{ fontSize: 10, fill: "#94a3b8" }} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#1e293b", border: "none", borderRadius: 8, fontSize: 12 }}
                labelStyle={{ color: "#94a3b8" }} itemStyle={{ color: "#22c55e" }} />
              <Bar dataKey="emails" fill="#22c55e" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        ) : (
          <div className="h-40 flex items-center justify-center text-slate-400 text-sm">No email activity yet</div>
        )}
      </div>

      {/* Two-col: Top Colleges + Quick Stats */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 mb-6">
        <div className="bg-white border border-slate-200 rounded-xl p-5">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400 mb-4">Top 10 Most Tracked Colleges</p>
          {stats?.top_colleges?.length > 0 ? (
            <div className="space-y-2.5">
              {stats.top_colleges.map((c, i) => {
                const pct = Math.round((c.count / stats.top_colleges[0].count) * 100);
                return (
                  <div key={i} className="flex items-center gap-3">
                    <span className="text-xs font-black text-slate-300 w-4 text-right flex-shrink-0">{i + 1}</span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-0.5">
                        <span className="text-xs font-semibold text-slate-700 truncate">{c.name}</span>
                        <span className="text-xs font-black text-slate-900 ml-2 flex-shrink-0">{c.count}</span>
                      </div>
                      <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
                        <div className="h-full bg-orange-400 rounded-full" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ) : (
            <p className="text-slate-400 text-sm text-center pt-6">No tracking data yet</p>
          )}
        </div>

        <div className="space-y-3">
          {[
            { icon: ArrowUpRight, label: "New users this month",  value: stats?.users?.new_30d ?? 0,   note: "last 30 days",          color: "text-orange-500" },
            { icon: Mail,         label: "Replies received",      value: stats?.emails?.total_received ?? 0, note: "from coaches",   color: "text-blue-500" },
            { icon: CircleDot,    label: "Active users (30d)",    value: stats?.users?.active_30d ?? 0, note: "logged in last 30 days", color: "text-green-500" },
            { icon: Star,         label: "Avg emails per user",   value: stats?.users?.total ? Math.round((stats.emails.total_sent / stats.users.total) * 10) / 10 : 0, note: "all-time average", color: "text-purple-500" },
          ].map(({ icon: Icon, label, value, note, color }) => (
            <div key={label} className="bg-white border border-slate-200 rounded-xl px-5 py-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <Icon className={`w-5 h-5 ${color}`} />
                <div>
                  <p className="text-sm font-bold text-slate-800">{label}</p>
                  <p className="text-xs text-slate-400">{note}</p>
                </div>
              </div>
              <span className="text-2xl font-black text-slate-900" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>{value}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
            All Users <span className="text-slate-900 font-black ml-1">{filteredUsers.length}</span>
          </p>
          <input
            data-testid="admin-user-search"
            type="text"
            value={search}
            onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..."
            className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-orange-500 outline-none w-60"
          />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {[
                  { key: "name", label: "User" },
                  { key: "subscription_tier", label: "Tier" },
                  { key: "created_at", label: "Joined" },
                  { key: "last_active", label: "Last Active" },
                  { key: "emails_sent", label: "Emails" },
                  { key: "colleges_tracked", label: "Tracked" },
                  { key: null, label: "Change Tier" },
                  { key: null, label: "" },
                ].map(col => (
                  <th key={col.label}
                    onClick={() => col.key && toggleSort(col.key)}
                    className={`text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap select-none ${col.key ? "cursor-pointer hover:text-slate-600" : ""}`}
                  >
                    <span className="flex items-center gap-1">{col.label}{col.key && <SortIcon k={col.key} />}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredUsers.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400 text-sm">
                  {search ? "No users match your search" : "No users yet"}
                </td></tr>
              ) : filteredUsers.map((u, idx) => (
                <tr key={u.user_id || u.email || idx} data-testid={`user-row-${u.user_id}`} className="hover:bg-slate-50 transition-colors">
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {u.picture
                        ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                        : <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-xs font-black flex-shrink-0">
                            {(u.name || u.email || "?")[0].toUpperCase()}
                          </div>
                      }
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-800 truncate max-w-[160px]">{u.name || "—"}</p>
                        <p className="text-xs text-slate-400 truncate max-w-[160px]">{u.email}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3"><TierBadge tier={u.subscription_tier} /></td>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                  <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{relTime(u.last_active)}</td>
                  <td className="px-4 py-3"><span className="font-bold text-slate-800">{u.emails_sent}</span><span className="text-xs text-slate-400 ml-1">sent</span></td>
                  <td className="px-4 py-3"><span className="font-bold text-slate-800">{u.colleges_tracked}</span><span className="text-xs text-slate-400 ml-1">colleges</span></td>
                  <td className="px-4 py-3"><TierSelect userId={u.user_id} current={u.subscription_tier} onChange={updateTier} /></td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <button
                        data-testid={`view-user-btn-${u.user_id}`}
                        onClick={() => navigate(`/admin/users/${u.user_id}`)}
                        className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 font-bold transition-colors"
                      >
                        View <ChevronRight className="w-3.5 h-3.5" />
                      </button>
                      <button
                        data-testid={`disable-user-btn-${u.user_id}`}
                        onClick={() => toggleDisableUser(u.user_id, u.is_disabled)}
                        className={`p-1 rounded transition-colors text-xs font-bold ${u.is_disabled ? "text-green-600 hover:bg-green-50" : "text-amber-500 hover:bg-amber-50"}`}
                        title={u.is_disabled ? "Enable user" : "Disable user"}
                      >
                        {u.is_disabled ? "Enable" : "Disable"}
                      </button>
                      {(u.emails_sent === 0 && u.colleges_tracked === 0) && (
                        <button
                          data-testid={`delete-user-btn-${u.user_id}`}
                          onClick={() => setConfirmDelete({ user_id: u.user_id, name: u.name, email: u.email })}
                          className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                          title="Delete user (no history)"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      </>)}

      {/* ── COACHES TAB ──────────────────────────────────────────── */}
      {activeTab === "coaches" && (
        <div>
          {/* Header + Filter */}
          <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
            <div>
              <h2 className="font-black text-slate-900 text-lg">Coach Accounts</h2>
              <p className="text-slate-500 text-sm">{coaches.length} coach{coaches.length !== 1 ? "es" : ""} registered</p>
            </div>
            <div className="flex gap-2">
              {["all", "pending", "verified", "rejected"].map(f => (
                <button key={f} onClick={() => setCoachFilter(f)}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase transition-all ${coachFilter === f ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700 bg-white border border-slate-200"}`}>
                  {f === "all" ? `All (${coaches.length})` : `${f} (${coaches.filter(c => c.verification_status === f).length})`}
                </button>
              ))}
            </div>
          </div>

          {!coachesLoaded ? (
            <div className="flex justify-center py-20 text-slate-400">Loading coaches...</div>
          ) : coaches.length === 0 ? (
            <div className="bg-white border border-slate-200 rounded-xl py-20 text-center text-slate-400">No coach accounts yet</div>
          ) : (
            <div className="space-y-3">
              {coaches
                .filter(c => coachFilter === "all" || c.verification_status === coachFilter)
                .map(c => (
                  <div key={c.id} data-testid={`coach-row-${c.id}`}
                    className="bg-white border border-slate-200 rounded-xl p-5">
                    <div className="flex items-start justify-between gap-4 flex-wrap">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1 flex-wrap">
                          <h3 className="font-black text-slate-900">{c.full_name}</h3>
                          <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${
                            c.verification_status === "verified" ? "bg-green-100 text-green-700" :
                            c.verification_status === "rejected" ? "bg-red-100 text-red-700" :
                            "bg-amber-100 text-amber-700"
                          }`}>{c.verification_status}</span>
                          <span className="text-xs text-slate-400 bg-slate-100 px-2 py-0.5 rounded-full">{c.primary_sport}</span>
                        </div>
                        <p className="text-slate-600 text-sm">{c.job_title} — {c.institution_name}</p>
                        <div className="flex flex-wrap gap-3 mt-1 text-xs text-slate-400">
                          <span>{c.email}</span>
                          <span>·</span>
                          <span>{c.division}</span>
                          {c.conference && <><span>·</span><span>{c.conference}</span></>}
                          <span>·</span>
                          <span>Applied {c.created_at ? new Date(c.created_at).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "—"}</span>
                        </div>
                        {c.verification_notes && (
                          <p className="text-slate-500 text-xs mt-2 italic">{c.verification_notes}</p>
                        )}
                      </div>
                      {/* Action buttons */}
                      {c.verification_status !== "verified" && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            data-testid={`approve-coach-${c.id}`}
                            onClick={() => setVerifyingCoach({ ...c, action: "approve" })}
                            className="flex items-center gap-1.5 bg-green-600 hover:bg-green-500 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors">
                            <CheckCircle2 className="w-3.5 h-3.5" /> Approve
                          </button>
                          <button
                            data-testid={`reject-coach-${c.id}`}
                            onClick={() => setVerifyingCoach({ ...c, action: "reject" })}
                            className="flex items-center gap-1.5 bg-red-600 hover:bg-red-500 text-white text-xs font-bold px-4 py-2 rounded-lg transition-colors">
                            <XCircle className="w-3.5 h-3.5" /> Reject
                          </button>
                        </div>
                      )}
                      {c.verification_status === "verified" && (
                        <div className="flex gap-2 flex-shrink-0">
                          <button
                            data-testid={`revoke-coach-${c.id}`}
                            onClick={() => setVerifyingCoach({ ...c, action: "reject" })}
                            className="text-xs font-bold text-slate-400 hover:text-red-500 border border-slate-200 px-3 py-1.5 rounded-lg transition-colors">
                            Revoke
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                ))}
            </div>
          )}

          {/* Verify/Reject Confirm Modal */}
          {verifyingCoach && (
            <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl border border-slate-200 shadow-2xl w-full max-w-md p-6">
                <h3 className="font-black text-slate-900 text-lg mb-1">
                  {verifyingCoach.action === "approve" ? "Approve" : "Reject"} Coach
                </h3>
                <p className="text-slate-600 text-sm mb-4">
                  {verifyingCoach.action === "approve"
                    ? `This will grant ${verifyingCoach.full_name} full access to search and message players.`
                    : `This will block ${verifyingCoach.full_name} from accessing the coach portal.`}
                </p>
                <div className="mb-4">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wider mb-1.5">Notes (optional)</label>
                  <input
                    value={verifyForm.notes}
                    onChange={e => setVerifyForm({ notes: e.target.value })}
                    placeholder="Internal notes about this decision..."
                    className="w-full border border-slate-200 rounded-lg px-3 py-2.5 text-sm focus:outline-none focus:ring-2 focus:ring-orange-400"
                    data-testid="verify-coach-notes"
                  />
                </div>
                <div className="flex gap-3">
                  <button
                    data-testid="confirm-verify-coach-btn"
                    onClick={async () => {
                      try {
                        await adminReq("patch", `/admin/coaches/${verifyingCoach.id}/verify`, {
                          action: verifyingCoach.action,
                          notes: verifyForm.notes,
                        });
                        toast.success(`Coach ${verifyingCoach.action === "approve" ? "approved" : "rejected"}`);
                        setCoaches(prev => prev.map(c =>
                          c.id === verifyingCoach.id
                            ? { ...c, verification_status: verifyingCoach.action === "approve" ? "verified" : "rejected", verification_notes: verifyForm.notes }
                            : c
                        ));
                        setVerifyingCoach(null);
                        setVerifyForm({ notes: "" });
                      } catch { toast.error("Failed to update coach status"); }
                    }}
                    className={`flex-1 text-white font-black py-3 rounded-xl transition-colors ${verifyingCoach.action === "approve" ? "bg-green-600 hover:bg-green-500" : "bg-red-600 hover:bg-red-500"}`}>
                    {verifyingCoach.action === "approve" ? "Approve Coach" : "Reject Coach"}
                  </button>
                  <button onClick={() => { setVerifyingCoach(null); setVerifyForm({ notes: "" }); }}
                    className="px-5 py-3 text-slate-500 hover:text-slate-700 hover:bg-slate-100 rounded-xl transition-colors font-semibold text-sm">
                    Cancel
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── USERS TAB ──────────────────────────────────────────── */}
      {activeTab === "users" && (
      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="px-5 py-4 border-b border-slate-100 flex items-center justify-between flex-wrap gap-3">
          <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
            All Users <span className="text-slate-900 font-black ml-1">{filteredUsers.length}</span>
          </p>
          <input data-testid="admin-user-search" type="text" value={search} onChange={e => setSearch(e.target.value)}
            placeholder="Search by name or email..." className="border border-slate-200 rounded-lg px-3 py-1.5 text-sm text-slate-700 focus:ring-2 focus:ring-orange-500 outline-none w-60" />
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-slate-50 border-b border-slate-100">
                {[
                  { key: "name", label: "User" }, { key: "subscription_tier", label: "Tier" },
                  { key: "created_at", label: "Joined" }, { key: "last_active", label: "Last Active" },
                  { key: "emails_sent", label: "Emails" }, { key: "colleges_tracked", label: "Tracked" },
                  { key: null, label: "Change Tier" }, { key: null, label: "" },
                ].map(col => (
                  <th key={col.label} onClick={() => col.key && toggleSort(col.key)}
                    className={`text-left px-4 py-3 text-xs font-bold text-slate-400 uppercase tracking-wider whitespace-nowrap select-none ${col.key ? "cursor-pointer hover:text-slate-600" : ""}`}>
                    <span className="flex items-center gap-1">{col.label}{col.key && <SortIcon k={col.key} />}</span>
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {filteredUsers.length === 0
                ? <tr><td colSpan={8} className="px-4 py-10 text-center text-slate-400 text-sm">{search ? "No users match" : "No users yet"}</td></tr>
                : filteredUsers.map((u, idx) => (
                  <tr key={u.user_id || u.email || idx} data-testid={`user-row-${u.user_id}`} className="hover:bg-slate-50 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        {u.picture ? <img src={u.picture} alt="" className="w-8 h-8 rounded-full object-cover flex-shrink-0" />
                          : <div className="w-8 h-8 rounded-full bg-orange-100 flex items-center justify-center text-orange-600 text-xs font-black flex-shrink-0">{(u.name || u.email || "?")[0].toUpperCase()}</div>}
                        <div className="min-w-0">
                          <p className="font-semibold text-slate-800 truncate max-w-[160px]">{u.name || "—"}</p>
                          <p className="text-xs text-slate-400 truncate max-w-[160px]">{u.email}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3"><TierBadge tier={u.subscription_tier} /></td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{fmtDate(u.created_at)}</td>
                    <td className="px-4 py-3 text-xs text-slate-500 whitespace-nowrap">{relTime(u.last_active)}</td>
                    <td className="px-4 py-3"><span className="font-bold text-slate-800">{u.emails_sent}</span><span className="text-xs text-slate-400 ml-1">sent</span></td>
                    <td className="px-4 py-3"><span className="font-bold text-slate-800">{u.colleges_tracked}</span><span className="text-xs text-slate-400 ml-1">colleges</span></td>
                    <td className="px-4 py-3"><TierSelect userId={u.user_id} current={u.subscription_tier} onChange={updateTier} /></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2">
                        <button data-testid={`view-user-btn-${u.user_id}`} onClick={() => navigate(`/admin/users/${u.user_id}`)}
                          className="flex items-center gap-1 text-xs text-orange-600 hover:text-orange-800 font-bold transition-colors">
                          View <ChevronRight className="w-3.5 h-3.5" />
                        </button>
                        <button
                          data-testid={`disable-user-btn-${u.user_id}`}
                          onClick={() => toggleDisableUser(u.user_id, u.is_disabled)}
                          className={`p-1 rounded transition-colors text-xs font-bold ${u.is_disabled ? "text-green-600 hover:bg-green-50" : "text-amber-500 hover:bg-amber-50"}`}
                          title={u.is_disabled ? "Enable user" : "Disable user"}
                        >
                          {u.is_disabled ? "Enable" : "Disable"}
                        </button>
                        {(u.emails_sent === 0 && u.colleges_tracked === 0) && (
                          <button
                            data-testid={`delete-user-btn-${u.user_id}`}
                            onClick={() => setConfirmDelete({ user_id: u.user_id, name: u.name, email: u.email })}
                            className="p-1 text-slate-300 hover:text-red-500 hover:bg-red-50 rounded transition-colors"
                            title="Delete user (no history)"
                          >
                            <Trash2 className="w-3.5 h-3.5" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
            </tbody>
          </table>
        </div>
      </div>
      )}

      {/* ── REPORTS TAB ───────────────────────────────────────── */}
      {activeTab === "reports" && (() => {
        const REPORT_STATUS = {
          pending:      { label: "Pending",      color: "bg-yellow-100 text-yellow-700", icon: Clock },
          investigating:{ label: "Investigating", color: "bg-blue-100 text-blue-700",    icon: AlertTriangle },
          fixed:        { label: "Fixed",        color: "bg-green-100 text-green-700",   icon: CheckCircle2 },
          invalid:      { label: "Not Valid",    color: "bg-slate-100 text-slate-600",   icon: XCircle },
        };
        const submitResolve = async (reportId) => {
          try {
            await adminReq("patch", `/admin/reports/${reportId}`, {
              status: resolveForm.status,
              admin_response: resolveForm.message,
            });
            setReports(prev => prev.map(r => r.id === reportId
              ? { ...r, status: resolveForm.status, admin_response: resolveForm.message }
              : r
            ));
            setResolvingId(null);
            setResolveForm({ status: "fixed", message: "" });
          } catch {}
        };

        return (
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <p className="text-xs font-bold uppercase tracking-wider text-slate-400">
                {reports.length} total · {reports.filter(r => r.status === "pending").length} pending
              </p>
            </div>
            {reports.length === 0 ? (
              <div className="bg-white border border-slate-200 rounded-xl py-14 text-center text-slate-400">
                <Flag className="w-8 h-8 mx-auto mb-2 opacity-30" />
                <p className="text-sm">No reports submitted yet</p>
              </div>
            ) : reports.map(r => {
              const sCfg = REPORT_STATUS[r.status] || REPORT_STATUS.pending;
              const SIcon = sCfg.icon;
              return (
                <div key={r.id} data-testid={`report-card-${r.id}`} className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="flex items-start justify-between gap-4 p-5">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap mb-1">
                        <span className={`inline-flex items-center gap-1 text-xs font-bold px-2.5 py-1 rounded-full ${sCfg.color}`}>
                          <SIcon className="w-3 h-3" /> {sCfg.label}
                        </span>
                        <span className="text-xs text-slate-400">{fmtDate(r.created_at)}</span>
                      </div>
                      <p className="font-bold text-slate-800">{r.college_name} {r.coach_name ? `— ${r.coach_name}` : ""}</p>
                      <p className="text-xs text-slate-500 mt-0.5">
                        <span className="font-semibold">{r.user_name}</span> ({r.user_email}) · Issue: <span className="font-semibold">{r.issue_type}</span>
                      </p>
                      {r.correct_info && <p className="text-xs text-slate-600 mt-1.5 bg-slate-50 rounded px-2 py-1.5"><span className="font-semibold">Suggested info:</span> {r.correct_info}</p>}
                      {r.notes && <p className="text-xs text-slate-500 mt-1 italic">"{r.notes}"</p>}
                      {r.admin_response && (
                        <div className="mt-2 bg-green-50 border border-green-200 rounded px-3 py-2 text-xs text-green-800">
                          <span className="font-bold">Admin response sent:</span> {r.admin_response}
                        </div>
                      )}
                    </div>
                    {r.status === "pending" || r.status === "investigating" ? (
                      <div className="flex gap-2 flex-shrink-0">
                        {(r.issue_type?.toLowerCase().includes("email") || r.correct_info?.includes("@")) && (
                          <button
                            data-testid={`fix-email-btn-${r.id}`}
                            onClick={() => { setFixingEmail(r.id); setEmailFixValue(r.correct_info || ""); setResolvingId(null); }}
                            className="flex-shrink-0 text-xs bg-green-600 text-white font-bold px-3 py-2 rounded-lg hover:bg-green-700 transition-all flex items-center gap-1"
                          >
                            Fix Email
                          </button>
                        )}
                        <button
                          data-testid={`resolve-btn-${r.id}`}
                          onClick={() => { setResolvingId(r.id); setResolveForm({ status: "fixed", message: "" }); setFixingEmail(null); }}
                          className="flex-shrink-0 text-xs bg-slate-900 text-white font-bold px-3 py-2 rounded-lg hover:bg-slate-700 transition-all"
                        >
                          Respond
                        </button>
                      </div>
                    ) : emailFixDone === r.id ? (
                      <span className="text-xs text-green-600 font-bold">Email Fixed</span>
                    ) : null}
                  </div>

                  {/* Inline fix-email form */}
                  {fixingEmail === r.id && (
                    <div className="border-t border-green-100 p-5 bg-green-50 space-y-3">
                      <p className="text-xs font-bold text-green-800 uppercase tracking-wide">
                        Update coach email — {r.coach_name || r.college_name}
                      </p>
                      <div className="flex gap-2">
                        <input
                          data-testid={`fix-email-input-${r.id}`}
                          type="email"
                          value={emailFixValue}
                          onChange={e => setEmailFixValue(e.target.value)}
                          placeholder="Enter correct email address..."
                          className="flex-1 border border-green-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-green-500 outline-none bg-white"
                        />
                        <button
                          data-testid={`confirm-fix-email-${r.id}`}
                          onClick={() => applyEmailFix(r)}
                          disabled={emailFixSaving || !emailFixValue.trim()}
                          className="bg-green-600 text-white text-xs font-bold px-4 py-2 rounded-lg hover:bg-green-700 transition-all disabled:opacity-60"
                        >
                          {emailFixSaving ? "Saving..." : "Apply Fix"}
                        </button>
                        <button onClick={() => setFixingEmail(null)}
                          className="text-slate-500 text-xs font-bold px-3 py-2 rounded-lg hover:bg-slate-100 transition-all">
                          Cancel
                        </button>
                      </div>
                      <p className="text-xs text-green-700">This updates the email in the database immediately and marks the report as fixed.</p>
                    </div>
                  )}

                  {/* Inline response form */}
                  {resolvingId === r.id && (
                    <div className="border-t border-slate-100 p-5 bg-slate-50 space-y-3">
                      <div className="flex gap-2 flex-wrap">
                        {["investigating", "fixed", "invalid"].map(s => (
                          <button key={s} onClick={() => setResolveForm(f => ({ ...f, status: s }))}
                            className={`text-xs font-bold px-3 py-1.5 rounded-lg border transition-all capitalize ${resolveForm.status === s ? "bg-slate-900 text-white border-slate-900" : "bg-white text-slate-600 border-slate-200 hover:bg-slate-100"}`}>
                            {s}
                          </button>
                        ))}
                      </div>
                      <textarea
                        data-testid={`report-message-${r.id}`}
                        value={resolveForm.message}
                        onChange={e => setResolveForm(f => ({ ...f, message: e.target.value }))}
                        rows={3}
                        placeholder="Message to user (optional — will appear as in-app notification if filled in)"
                        className="w-full border border-slate-200 rounded-lg p-3 text-sm focus:ring-2 focus:ring-orange-500 outline-none resize-none bg-white"
                      />
                      <div className="flex gap-2">
                        <button data-testid={`submit-resolve-${r.id}`} onClick={() => submitResolve(r.id)}
                          className="bg-orange-500 text-white font-bold text-xs px-4 py-2 rounded-lg hover:bg-orange-600 transition-all">
                          Send & Update
                        </button>
                        <button onClick={() => setResolvingId(null)}
                          className="text-slate-500 font-bold text-xs px-4 py-2 rounded-lg hover:bg-slate-100 transition-all">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })()}

      {/* ── COLLEGES TAB ─────────────────────────────────────── */}
      {activeTab === "colleges" && (() => {
        const suspiciousPatterns = ["athletics@","info@","admin@","basketball@","sports@","recruiting@","coaches@","contact@"];
        const isSuspicious = (email, human_verified) => !human_verified && (!email || suspiciousPatterns.some(p => email.toLowerCase().startsWith(p)));

        const filtered = contacts.filter(c => {
          const matchSearch = !contactSearch || c.college_name.toLowerCase().includes(contactSearch.toLowerCase()) || c.coach_name.toLowerCase().includes(contactSearch.toLowerCase()) || c.email.toLowerCase().includes(contactSearch.toLowerCase());
          const matchFilter = contactFilter === "all" || (contactFilter === "suspicious" && isSuspicious(c.email, c.human_verified)) || (contactFilter === "ok" && !isSuspicious(c.email, c.human_verified));
          const matchGender = contactGender === "all" || c.programme_gender === contactGender;
          return matchSearch && matchFilter && matchGender;
        });

        const suspiciousCount = contacts.filter(c => isSuspicious(c.email, c.human_verified)).length;

        return (
          <div>
            {/* Bulk College Import/Export */}
            <div className="flex items-center gap-2 mb-4 flex-wrap p-4 bg-slate-50 border border-slate-200 rounded-xl">
              <span className="text-xs font-bold text-slate-600 uppercase tracking-wide mr-2">Colleges:</span>
              <button data-testid="export-colleges-btn" onClick={exportBulkColleges}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-900 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-all">
                <Download className="w-3.5 h-3.5" /> Export All Colleges
              </button>
              <label className="flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-all cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                {bulkImporting ? "Importing..." : "Import Colleges (CSV)"}
                <input type="file" accept=".csv" className="hidden" onChange={handleBulkCollegeImport} disabled={bulkImporting} />
              </label>
              <span className="text-xs text-slate-400">New colleges are added as <strong>Pending</strong> for review. Duplicates are detected and skipped.</span>
            </div>

            {/* Import result */}
            {bulkImportResult && (
              <div data-testid="bulk-import-result" className={`mb-4 p-4 rounded-xl border ${bulkImportResult.error ? "bg-red-50 border-red-200" : "bg-green-50 border-green-200"}`}>
                {bulkImportResult.error ? (
                  <div className="flex items-center gap-2 text-red-700 text-sm font-semibold">
                    <XCircle className="w-4 h-4" /> {bulkImportResult.error}
                  </div>
                ) : (
                  <>
                    <div className="flex items-center gap-3 mb-2">
                      <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                      <span className="font-bold text-green-800 text-sm">Import complete</span>
                      <span className="text-green-700 text-sm">
                        <strong>{bulkImportResult.inserted}</strong> college{bulkImportResult.inserted !== 1 ? "s" : ""} added (pending approval)
                        {bulkImportResult.errors > 0 && <span className="text-amber-600 ml-2">{bulkImportResult.errors} error{bulkImportResult.errors !== 1 ? "s" : ""}</span>}
                      </span>
                    </div>
                    {bulkImportResult.duplicates?.length > 0 && (
                      <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-2">
                        <p className="text-xs font-bold text-amber-800 flex items-center gap-1.5 mb-1">
                          <AlertTriangle className="w-3.5 h-3.5" />
                          {bulkImportResult.duplicates.length} duplicate{bulkImportResult.duplicates.length !== 1 ? "s" : ""} found and skipped (existing records not overwritten):
                        </p>
                        <p className="text-xs text-amber-700 leading-relaxed">{bulkImportResult.duplicates.map(d => d.name).join(", ")}</p>
                      </div>
                    )}
                  </>
                )}
                <button onClick={() => setBulkImportResult(null)} className="mt-2 text-slate-400 hover:text-slate-600 text-xs">Dismiss</button>
              </div>
            )}

            {/* Pending Colleges Section */}
            {(pendingColleges.length > 0 || pendingLoading) && (
              <div data-testid="pending-colleges-section" className="mb-6 bg-amber-50 border border-amber-200 rounded-xl overflow-hidden">
                <div className="flex items-center gap-2 px-4 py-3 bg-amber-100 border-b border-amber-200">
                  <Clock className="w-4 h-4 text-amber-600" />
                  <span className="text-sm font-bold text-amber-800">
                    Pending Approval — {pendingLoading ? "..." : pendingColleges.length} college{pendingColleges.length !== 1 ? "s" : ""} awaiting review
                  </span>
                </div>
                {pendingLoading ? (
                  <div className="p-6 text-center text-amber-600 text-sm">Loading pending colleges...</div>
                ) : (
                  <div className="divide-y divide-amber-100">
                    {pendingColleges.map(c => (
                      <div key={c.id} data-testid={`pending-college-${c.id}`} className="flex items-center gap-4 px-4 py-3 hover:bg-amber-50/80">
                        <div className="flex-1 min-w-0">
                          <p className="font-bold text-slate-800 text-sm truncate">{c.name}</p>
                          <p className="text-xs text-slate-500">{[c.division, c.conference, c.state].filter(Boolean).join(" · ")}</p>
                          {(c.coaches || []).slice(0, 1).map(coach => (
                            <p key={coach.id} className="text-xs text-slate-400">{coach.name} — {coach.email}</p>
                          ))}
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <button
                            onClick={() => approveCollege(c.id)}
                            disabled={approvingId === c.id}
                            data-testid={`approve-college-${c.id}`}
                            className="flex items-center gap-1.5 text-xs font-bold bg-green-600 hover:bg-green-500 text-white px-3 py-1.5 rounded-lg transition-colors disabled:opacity-50"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" />
                            {approvingId === c.id ? "..." : "Approve"}
                          </button>
                          <button
                            onClick={() => rejectCollege(c.id, c.name)}
                            data-testid={`reject-college-${c.id}`}
                            className="flex items-center gap-1.5 text-xs font-bold bg-red-50 hover:bg-red-100 text-red-600 border border-red-200 px-3 py-1.5 rounded-lg transition-colors"
                          >
                            <Trash2 className="w-3.5 h-3.5" /> Delete
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Stats bar */}
            <div className="flex items-center gap-4 mb-4 flex-wrap">
              <div className="bg-white border border-slate-200 rounded-lg px-4 py-2 text-sm">
                <span className="font-black text-slate-900">{contacts.length}</span>
                <span className="text-slate-500 ml-1">total coaches</span>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-2 text-sm">
                <span className="font-black text-amber-700">{suspiciousCount}</span>
                <span className="text-amber-600 ml-1">suspicious emails</span>
              </div>
              <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm">
                <span className="font-black text-green-700">{contacts.length - suspiciousCount}</span>
                <span className="text-green-600 ml-1">verified-looking</span>
              </div>
            </div>

            {/* Export / Import toolbar */}
            <div className="flex items-center gap-2 mb-4 flex-wrap">
              <button
                data-testid="export-suspicious-btn"
                onClick={() => exportContacts("suspicious")}
                className="flex items-center gap-2 px-4 py-2 bg-amber-500 hover:bg-amber-600 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-all"
              >
                <Download className="w-3.5 h-3.5" /> Export Suspicious ({suspiciousCount})
              </button>
              <button
                data-testid="export-all-btn"
                onClick={() => exportContacts("all")}
                className="flex items-center gap-2 px-4 py-2 bg-slate-700 hover:bg-slate-900 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-all"
              >
                <Download className="w-3.5 h-3.5" /> Export All
              </button>
              <label className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-all cursor-pointer">
                <Upload className="w-3.5 h-3.5" />
                {importing ? "Importing..." : "Import CSV"}
                <input type="file" accept=".csv" className="hidden" onChange={handleImport} disabled={importing} />
              </label>
              <span className="text-xs text-slate-400">Edit the exported CSV, then import to bulk-update</span>
            </div>

            {/* Import result banner */}
            {importResult && (
              <div data-testid="import-result" className={`mb-4 px-4 py-3 rounded-xl text-sm font-semibold border flex flex-wrap items-center gap-4 ${importResult.ok ? "bg-green-50 border-green-200 text-green-800" : "bg-red-50 border-red-200 text-red-800"}`}>
                {importResult.ok ? (
                  <>
                    <CheckCircle2 className="w-4 h-4 text-green-600 flex-shrink-0" />
                    <span>Import complete — <strong>{importResult.updated}</strong> updated, <strong>{importResult.skipped}</strong> skipped, <strong>{importResult.emails_deleted}</strong> bounced email{importResult.emails_deleted !== 1 ? "s" : ""} deleted</span>
                    {importResult.errors?.length > 0 && (
                      <span className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded px-2 py-1">
                        {importResult.errors.length} row error(s): {importResult.errors[0]}
                      </span>
                    )}
                  </>
                ) : (
                  <span>{importResult.error || "Import failed"}</span>
                )}
                <button onClick={() => setImportResult(null)} className="ml-auto text-slate-400 hover:text-slate-600 text-xs">Dismiss</button>
              </div>
            )}

            {/* Filters */}
            <div className="flex gap-2 mb-4 flex-wrap items-center">
              {/* Gender tabs */}
              <div className="flex rounded-lg border border-slate-200 overflow-hidden mr-2">
                {[
                  { id: "all",    label: "All" },
                  { id: "mens",   label: "Men's" },
                  { id: "womens", label: "Women's" },
                ].map(g => (
                  <button
                    key={g.id}
                    onClick={() => setContactGender(g.id)}
                    data-testid={`gender-tab-${g.id}`}
                    className={`px-4 py-2 text-xs font-bold transition-all ${contactGender === g.id ? "bg-slate-900 text-white" : "bg-white text-slate-500 hover:text-slate-800"}`}>
                    {g.label}
                  </button>
                ))}
              </div>
              <input
                data-testid="contact-search"
                value={contactSearch}
                onChange={e => setContactSearch(e.target.value)}
                placeholder="Search college, coach or email..."
                className="border border-slate-200 rounded-lg px-3 py-2 text-sm w-64 focus:ring-2 focus:ring-orange-400 outline-none bg-white"
              />
              {["all","suspicious","ok"].map(f => (
                <button key={f} onClick={() => setContactFilter(f)}
                  className={`px-3 py-2 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${contactFilter === f ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-500 hover:text-slate-800"}`}>
                  {f === "suspicious" ? `Suspicious (${suspiciousCount})` : f === "ok" ? "OK" : "All"}
                </button>
              ))}
              <span className="text-xs text-slate-400 ml-2">Showing {filtered.length} records</span>
            </div>

            {/* Table */}
            <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-slate-50 border-b border-slate-200">
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">College</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Programme</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Division</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Coach</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Email</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Last Verified</th>
                      <th className="px-4 py-3 text-left text-xs font-bold text-slate-500 uppercase tracking-wide">Action</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {filtered.map((c, i) => {
                      const key = `${c.college_id}-${c.coach_name}`;
                      const suspicious = isSuspicious(c.email, c.human_verified);
                      const done = inlineDone[key];
                      const isEditing = inlineEdit?.college_id === c.college_id && inlineEdit?.coach_name === c.coach_name;
                      return (
                        <tr key={i} className={suspicious && !done ? "bg-amber-50" : ""}>
                          <td className="px-4 py-3 font-semibold text-slate-800 text-xs max-w-[160px] truncate">{c.college_name}</td>
                          <td className="px-4 py-3 text-xs">
                            <span className={`px-2 py-0.5 rounded-full font-semibold ${c.programme_gender === "womens" ? "bg-pink-100 text-pink-700" : "bg-blue-100 text-blue-700"}`}>
                              {c.programme_gender === "womens" ? "Women's" : "Men's"}
                            </span>
                            {c.from_staging && (
                              <span className="ml-1 px-1.5 py-0.5 rounded text-[10px] font-semibold bg-amber-100 text-amber-600">staging</span>
                            )}
                          </td>
                          <td className="px-4 py-3 text-xs text-slate-500">{c.division}</td>
                          <td className="px-4 py-3 text-xs text-slate-700">{c.coach_name}<br/><span className="text-slate-400">{c.coach_title}</span></td>
                          <td className="px-4 py-3">
                            {isEditing ? (
                              <div className="flex flex-col gap-2">
                                <input
                                  data-testid={`inline-name-input-${i}`}
                                  type="text"
                                  value={inlineNameValue}
                                  onChange={e => setInlineNameValue(e.target.value)}
                                  placeholder="Coach name..."
                                  className="border border-slate-300 rounded-lg px-2 py-1 text-xs w-52 focus:ring-2 focus:ring-orange-400 outline-none"
                                />
                                <div className="flex items-center gap-2">
                                  <input
                                    data-testid={`inline-email-input-${i}`}
                                    type="email"
                                    value={inlineValue}
                                    onChange={e => setInlineValue(e.target.value)}
                                    placeholder="Email address..."
                                    className="border border-orange-300 rounded-lg px-2 py-1 text-xs w-52 focus:ring-2 focus:ring-orange-400 outline-none"
                                    autoFocus
                                    onKeyDown={e => e.key === "Enter" && saveInlineEmail()}
                                  />
                                  <button onClick={saveInlineEmail} disabled={inlineSaving}
                                    className="bg-green-600 text-white text-xs font-bold px-2 py-1 rounded hover:bg-green-700 disabled:opacity-60">
                                    {inlineSaving ? "..." : "Save"}
                                  </button>
                                  <button onClick={() => { setInlineEdit(null); setInlineNameValue(""); setInlineValue(""); setInlineLvValue(""); setInlineSaveError(""); }}
                                    className="text-slate-400 text-xs hover:text-slate-600">✕</button>
                                </div>
                                {inlineSaveError && (
                                  <p className="text-xs text-red-500 font-semibold mt-1" data-testid="inline-save-error">{inlineSaveError}</p>
                                )}
                                <div className="flex items-center gap-2">
                                  <label className="text-xs text-slate-500 font-semibold w-24 flex-shrink-0">Last Verified:</label>
                                  <input
                                    data-testid={`inline-lv-input-${i}`}
                                    type="date"
                                    value={inlineLvValue}
                                    onChange={e => setInlineLvValue(e.target.value)}
                                    className="border border-slate-300 rounded-lg px-2 py-1 text-xs focus:ring-2 focus:ring-orange-400 outline-none"
                                  />
                                  <button onClick={() => setInlineLvValue(new Date().toISOString().slice(0,10))}
                                    className="text-xs text-green-600 font-semibold hover:text-green-800">Today</button>
                                </div>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                {suspicious && !done ? (
                                  <span className="inline-flex items-center gap-1 text-xs text-amber-700 font-semibold">
                                    <AlertTriangle className="w-3 h-3" /> {c.email || "No email"}
                                  </span>
                                ) : (
                                  <span className={`text-xs font-mono ${done ? "text-green-600 font-semibold" : "text-slate-600"}`}>
                                    {done ? "✓ " : ""}{c.email || "—"}
                                  </span>
                                )}
                              </div>
                            )}
                          </td>
                          <td className="px-4 py-3">
                            {(() => {
                              const lv = c.last_verified;
                              if (!lv) return <span className="text-xs text-red-500 font-semibold">Never</span>;
                              const days = Math.floor((Date.now() - new Date(lv)) / 86400000);
                              const color = days > 180 ? "text-amber-600" : "text-green-600";
                              return <span className={`text-xs font-semibold ${color}`}>{lv}<br/><span className="text-slate-400 font-normal">{days}d ago</span></span>;
                            })()}
                          </td>
                          <td className="px-4 py-3">
                            {!isEditing && (
                              <div className="flex items-center gap-2">
                                <button
                                  data-testid={`edit-contact-${i}`}
                                  onClick={() => { setInlineEdit({ college_id: c.college_id, coach_id: c.coach_id, coach_name: c.coach_name, current_email: c.email, last_verified: c.last_verified, programme_gender: c.programme_gender }); setInlineNameValue(c.coach_name); setInlineValue(c.email); setInlineLvValue(c.last_verified || ""); setInlineSaveError(""); }}
                                  className="text-xs text-orange-600 hover:text-orange-800 font-bold transition-colors"
                                >
                                  Edit Coach
                                </button>
                                <span className="text-slate-200">|</span>
                                <button
                                  data-testid={`edit-college-${i}`}
                                  onClick={() => openEditCollege(c.college_id)}
                                  className="text-xs text-blue-600 hover:text-blue-800 font-bold transition-colors"
                                >
                                  Edit College
                                </button>
                                <span className="text-slate-200">|</span>
                                <button
                                  data-testid={`delete-coach-${i}`}
                                  onClick={() => setConfirmDeleteCoach({ college_id: c.college_id, coach_name: c.coach_name, college_name: c.college_name, programme_gender: c.programme_gender || null })}
                                  className="text-xs text-red-400 hover:text-red-600 font-bold transition-colors"
                                  title="Delete coach"
                                >
                                  Delete
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        );
      })()}

      {/* ── SETTINGS TAB ─────────────────────────────────────── */}
      {activeTab === "pricing" && (
        <div className="max-w-2xl space-y-6">
          <div className="flex items-center gap-2 mb-2">
            <span className="text-xs font-bold uppercase tracking-wider text-orange-600">Pricing Management</span>
          </div>
          <h2 className="text-xl font-black text-slate-900 mb-4" style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
            Subscription Plans
          </h2>

          {pricingSaved && (
            <div className="flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm font-semibold px-4 py-3 rounded-lg">
              <CheckCircle2 className="w-4 h-4" />
              Pricing updated — changes are live immediately.
            </div>
          )}

          {pricingPlans.length === 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-700">
              Run <code className="bg-amber-100 px-1 rounded">supabase_migration_v3.sql</code> in Supabase to enable pricing management.
            </div>
          )}

          {pricingPlans.map(plan => {
            const isEditing = editingPlan === plan.tier;
            const features = Array.isArray(plan.features) ? plan.features : JSON.parse(plan.features || "[]");
            return (
              <div key={plan.tier} className="bg-white border border-slate-200 rounded-xl p-6">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-3">
                    <span className={`inline-flex items-center gap-1.5 text-xs font-bold px-2.5 py-1 rounded-full ${TIER_CONFIG[plan.tier]?.bg || "bg-slate-100 text-slate-600"}`}>
                      <span className={`w-1.5 h-1.5 rounded-full ${TIER_CONFIG[plan.tier]?.dot || "bg-slate-400"}`} />
                      {plan.name}
                    </span>
                    <span className="text-lg font-black text-slate-900">{plan.currency === "GBP" ? "£" : plan.currency}{Number(plan.price_monthly).toFixed(2)}<span className="text-sm text-slate-400 font-normal">/mo</span></span>
                  </div>
                  <button
                    onClick={() => {
                      setEditingPlan(plan.tier);
                      setPricingForm({
                        name: plan.name,
                        price_monthly: plan.price_monthly,
                        price_annual: plan.price_annual ?? "",
                        currency: plan.currency,
                        description: plan.description,
                        features: features.join("\n"),
                      });
                    }}
                    data-testid={`edit-pricing-${plan.tier}`}
                    className="text-xs font-bold text-orange-600 hover:text-orange-800 px-3 py-1.5 border border-orange-200 hover:border-orange-400 rounded-lg transition-colors"
                  >
                    Edit
                  </button>
                </div>

                {!isEditing ? (
                  <>
                    <p className="text-sm text-slate-500 mb-3">{plan.description}</p>
                    <ul className="space-y-1">
                      {features.map((f, i) => <li key={i} className="text-xs text-slate-600 flex items-center gap-1.5"><span className="w-1 h-1 rounded-full bg-orange-400 flex-shrink-0" />{f}</li>)}
                    </ul>
                  </>
                ) : (
                  <div className="space-y-3">
                    <div className="grid grid-cols-2 gap-3">
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Plan Name</label>
                        <input data-testid={`pricing-name-${plan.tier}`} value={pricingForm.name || ""} onChange={e => setPricingForm(p => ({ ...p, name: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Currency</label>
                        <select data-testid={`pricing-currency-${plan.tier}`} value={pricingForm.currency || "GBP"} onChange={e => setPricingForm(p => ({ ...p, currency: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-2 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none">
                          <option value="GBP">GBP £</option>
                          <option value="USD">USD $</option>
                          <option value="EUR">EUR €</option>
                        </select>
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Monthly Price</label>
                        <input data-testid={`pricing-price-${plan.tier}`} type="number" step="0.01" min="0" value={pricingForm.price_monthly || ""} onChange={e => setPricingForm(p => ({ ...p, price_monthly: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none" />
                      </div>
                      <div>
                        <label className="block text-xs font-semibold text-slate-500 mb-1">Annual Price {plan.tier !== "season_pass" && <span className="text-slate-400 font-normal">(one-time yearly)</span>}</label>
                        <input data-testid={`pricing-annual-${plan.tier}`} type="number" step="0.01" min="0" value={pricingForm.price_annual ?? ""} onChange={e => setPricingForm(p => ({ ...p, price_annual: e.target.value }))} placeholder="e.g. 79.00" className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none" />
                      </div>
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Description</label>
                      <input data-testid={`pricing-desc-${plan.tier}`} value={pricingForm.description || ""} onChange={e => setPricingForm(p => ({ ...p, description: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none" />
                    </div>
                    <div>
                      <label className="block text-xs font-semibold text-slate-500 mb-1">Features (one per line)</label>
                      <textarea data-testid={`pricing-features-${plan.tier}`} rows={5} value={pricingForm.features || ""} onChange={e => setPricingForm(p => ({ ...p, features: e.target.value }))} className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-500 outline-none resize-none" />
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => savePricing(plan.tier)}
                        disabled={pricingSaving}
                        data-testid={`pricing-save-${plan.tier}`}
                        className="bg-orange-500 hover:bg-orange-600 text-white font-bold text-sm px-4 py-2 rounded-lg transition-colors disabled:opacity-60"
                      >
                        {pricingSaving ? "Saving..." : "Save Changes"}
                      </button>
                      <button onClick={() => setEditingPlan(null)} className="text-slate-500 hover:text-slate-700 text-sm font-semibold px-4 py-2 rounded-lg border border-slate-200 hover:border-slate-400 transition-colors">Cancel</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {activeTab === "settings" && (
        <div className="max-w-xl">
          <div className="bg-white border border-slate-200 rounded-xl p-6">
            <div className="flex items-center gap-2 mb-1">
              <Settings className="w-4 h-4 text-orange-500" />
              <span className="text-xs font-bold uppercase tracking-wider text-orange-600">App Settings</span>
            </div>
            <h2 className="text-xl font-black text-slate-900 mb-5" style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
              Feature Flags
            </h2>

            {/* January / Spring Enrollment toggle */}
            <div className="flex items-start justify-between gap-4 py-4 border-b border-slate-100">
              <div className="flex-1">
                <p className="text-sm font-bold text-slate-800">January / Spring Enrollment Messaging</p>
                <p className="text-xs text-slate-400 mt-0.5 leading-snug">
                  When ON: the enrollment card appears in Compose for eligible divisions (NAIA, JUCO, DII/DIII foreign-friendly), exactly as today.
                  When OFF: the card is hidden for every user regardless of division, and January context is never injected into AI drafts.
                </p>
              </div>
              <button
                data-testid="jan-enrollment-toggle-btn"
                disabled={savingSettings}
                onClick={async () => {
                  const newVal = !appSettings.january_enrollment_enabled;
                  setSavingSettings(true);
                  try {
                    await adminReq("patch", "/admin/settings/january-enrollment", { enabled: newVal });
                    setAppSettings(prev => ({ ...prev, january_enrollment_enabled: newVal }));
                    setSettingsSaved(true);
                    setTimeout(() => setSettingsSaved(false), 3000);
                  } catch {
                    toast.error("Save failed — please try again.");
                  }
                  setSavingSettings(false);
                }}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 focus:outline-none disabled:opacity-60 ${
                  appSettings.january_enrollment_enabled ? "bg-orange-500" : "bg-slate-300"
                }`}
                role="switch"
                aria-checked={appSettings.january_enrollment_enabled}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow transition duration-200 ${
                    appSettings.january_enrollment_enabled ? "translate-x-5" : "translate-x-0"
                  }`}
                />
              </button>
            </div>

            {settingsSaved && (
              <div data-testid="settings-saved-banner" className="mt-4 flex items-center gap-2 bg-green-50 border border-green-200 text-green-700 text-sm font-semibold px-4 py-3 rounded-lg">
                <CheckCircle2 className="w-4 h-4" />
                Settings saved — changes are live for all users immediately.
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── PROMO CODES TAB ───────────────────────────────────────── */}
      {activeTab === "promos" && <AdminPromoCodes />}

      {/* ── LEGAL TAB ─────────────────────────────────────────── */}
      {activeTab === "legal" && <AdminLegalEditor />}

      {/* ── DATA HEALTH TAB ──────────────────────────────────────── */}
      {activeTab === "data-health" && (
        <AdminDataHealth adminToken={localStorage.getItem("cb_admin_token")} />
      )}

      {activeTab === "scraper" && (
        <AdminScraperPanel adminToken={localStorage.getItem("cb_admin_token")} />
      )}

      {/* ── FUNNEL TAB ─────────────────────────────────────────── */}
      {activeTab === "funnel" && (
        <div className="space-y-6">
          {!funnel ? (
            <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : (<>
            {/* KPI Row */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {[
                { label: "Trial → Paid Rate", value: `${funnel.trial_to_paid_rate}%`, color: "text-green-600", icon: Target },
                { label: "Sign-up → Activated", value: `${funnel.signup_to_activated_rate}%`, color: "text-blue-600", icon: Zap },
                { label: "Total Sign-ups", value: funnel.funnel[0]?.count ?? 0, color: "text-slate-900", icon: Users },
                { label: "Paid Users", value: funnel.funnel[3]?.count ?? 0, color: "text-orange-600", icon: Star },
              ].map(k => (
                <div key={k.label} className="bg-white border border-slate-200 rounded-xl p-5">
                  <div className="flex items-center gap-2 mb-2"><k.icon className={`w-4 h-4 ${k.color}`} /><span className="text-xs font-bold uppercase tracking-wider text-slate-500">{k.label}</span></div>
                  <p className={`text-3xl font-black ${k.color}`}>{k.value}</p>
                </div>
              ))}
            </div>

            {/* Visual Funnel */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-black text-slate-900 mb-6 uppercase tracking-wide text-sm">Conversion Funnel</h3>
              <div className="space-y-3">
                {funnel.funnel.map((stage, i) => {
                  const colors = ["bg-blue-500","bg-indigo-500","bg-orange-500","bg-green-500"];
                  const next = funnel.funnel[i + 1];
                  const dropOff = next ? stage.count - next.count : 0;
                  return (
                    <div key={stage.stage}>
                      <div className="flex items-center gap-4 mb-1">
                        <span className="text-xs font-bold uppercase tracking-wider text-slate-500 w-24">{stage.stage}</span>
                        <div className="flex-1 bg-slate-100 rounded-full h-8 relative overflow-hidden">
                          <div className={`h-full ${colors[i]} rounded-full transition-all duration-700 flex items-center px-3`} style={{ width: `${Math.max(stage.pct, 4)}%` }}>
                            <span className="text-white text-xs font-black">{stage.count}</span>
                          </div>
                        </div>
                        <span className="text-sm font-bold text-slate-700 w-12 text-right">{stage.pct}%</span>
                      </div>
                      {next && <p className="text-xs text-red-400 pl-28 mb-1">▼ {dropOff} dropped off ({(100 - funnel.funnel[i+1].pct).toFixed(1)}% drop)</p>}
                    </div>
                  );
                })}
              </div>
            </div>

            {/* 30-day Signup Trend */}
            <div className="bg-white border border-slate-200 rounded-xl p-6">
              <h3 className="font-black text-slate-900 mb-4 uppercase tracking-wide text-sm">Sign-up Trend — Last 30 Days</h3>
              <AreaChart width={700} height={180} data={funnel.signup_trend} margin={{ top: 4, right: 20, bottom: 0, left: 0 }}>
                <defs><linearGradient id="signupGrad" x1="0" y1="0" x2="0" y2="1"><stop offset="5%" stopColor="#f97316" stopOpacity={0.3}/><stop offset="95%" stopColor="#f97316" stopOpacity={0}/></linearGradient></defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                <Tooltip />
                <Area type="monotone" dataKey="signups" stroke="#f97316" fill="url(#signupGrad)" strokeWidth={2} />
              </AreaChart>
            </div>

            {/* Lead Source Breakdown */}
            {funnel.lead_sources && (
              <div className="bg-white border border-slate-200 rounded-xl p-6" data-testid="admin-lead-source-chart">
                <h3 className="font-black text-slate-900 mb-1 uppercase tracking-wide text-sm">Acquisition Channels</h3>
                <p className="text-xs text-slate-400 mb-5">How users heard about CourtBound</p>
                {funnel.lead_sources.every(s => s.count === 0) ? (
                  <p className="text-sm text-slate-400 text-center py-6">No lead source data yet — data appears as users complete onboarding.</p>
                ) : (
                  <div className="space-y-3">
                    {funnel.lead_sources
                      .filter(s => s.count > 0)
                      .sort((a, b) => b.count - a.count)
                      .map(s => {
                        const total = funnel.lead_sources.reduce((acc, x) => acc + x.count, 0);
                        const pct = total ? Math.round((s.count / total) * 100) : 0;
                        const colorMap = { Instagram: "#e11d48", Clubs: "#7c3aed", Direct: "#0ea5e9", Referral: "#16a34a", Other: "#94a3b8" };
                        const color = colorMap[s.source] || "#f97316";
                        return (
                          <div key={s.source} data-testid={`lead-source-row-${s.source.toLowerCase()}`}>
                            <div className="flex items-center justify-between mb-1">
                              <span className="text-xs font-bold text-slate-600 uppercase tracking-wider">{s.source}</span>
                              <span className="text-xs font-black text-slate-800">{s.count} <span className="font-normal text-slate-400">({pct}%)</span></span>
                            </div>
                            <div className="h-7 bg-slate-100 rounded-lg overflow-hidden">
                              <div
                                className="h-full rounded-lg flex items-center px-3 transition-all duration-700"
                                style={{ width: `${Math.max(pct, 3)}%`, background: color }}
                              >
                                {pct >= 8 && <span className="text-white text-xs font-black">{pct}%</span>}
                              </div>
                            </div>
                          </div>
                        );
                      })}
                  </div>
                )}
              </div>
            )}
          </>)}
        </div>
      )}

      {/* ── COACH OUTREACH TAB ─────────────────────────────────── */}
      {activeTab === "coach-outreach" && <AdminCoachOutreach />}

      {/* ── OUTREACH TAB ───────────────────────────────────────── */}
      {activeTab === "outreach" && (
        <div className="space-y-4">
          {/* Sub-tab toggle */}
          <div className="flex items-center gap-1 border-b border-slate-200 pb-2">
            {[{ id: "analytics", label: "Analytics" }, { id: "growth", label: "Growth Tracker" }].map(st => (
              <button key={st.id}
                onClick={() => {
                  setOutreachSubTab(st.id);
                  if (st.id === "growth" && !growthTargetsLoaded) loadGrowthTargets();
                  if (st.id === "growth" && !contentBankLoaded) loadContentBank();
                }}
                className={`px-4 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all
                  ${outreachSubTab === st.id ? "bg-slate-900 text-white" : "text-slate-500 hover:text-slate-700"}`}
                data-testid={`outreach-subtab-${st.id}`}>
                {st.label}
              </button>
            ))}
          </div>

          {/* ── Analytics sub-tab ── */}
          {outreachSubTab === "analytics" && (
            <div className="space-y-6">
              {!outreach ? (
                <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
              ) : (<>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {[
                    { label: "Emails Sent", value: outreach.aggregate.total_sent, color: "text-slate-900", icon: Send },
                    { label: "Replies Received", value: outreach.aggregate.total_received, color: "text-blue-600", icon: Mail },
                    { label: "Reply Rate", value: `${outreach.aggregate.reply_rate}%`, color: outreach.aggregate.reply_rate >= 10 ? "text-green-600" : "text-orange-500", icon: TrendingUp },
                    { label: "Open Rate", value: "—", color: "text-slate-400", icon: Activity },
                  ].map(k => (
                    <div key={k.label} className="bg-white border border-slate-200 rounded-xl p-5">
                      <div className="flex items-center gap-2 mb-2"><k.icon className={`w-4 h-4 ${k.color}`} /><span className="text-xs font-bold uppercase tracking-wider text-slate-500">{k.label}</span></div>
                      <p className={`text-3xl font-black ${k.color}`}>{k.value}</p>
                      {k.label === "Open Rate" && <p className="text-xs text-slate-400 mt-1">Requires tracking pixel</p>}
                    </div>
                  ))}
                </div>
                {outreach.aggregate.reply_rate >= 10 && (
                  <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-start gap-3">
                    <CheckCircle2 className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                    <div>
                      <p className="font-bold text-green-800 text-sm">Strong outreach performance</p>
                      <p className="text-green-700 text-sm">Users are achieving a <strong>{outreach.aggregate.reply_rate}% reply rate</strong> — above the 10% industry average for cold coach outreach.</p>
                    </div>
                  </div>
                )}
                <div className="bg-white border border-slate-200 rounded-xl p-6">
                  <h3 className="font-black text-slate-900 mb-4 uppercase tracking-wide text-sm">Email Activity — Last 14 Days</h3>
                  <BarChart width={700} height={180} data={outreach.email_trend} margin={{ top: 4, right: 20, bottom: 0, left: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                    <XAxis dataKey="date" tick={{ fontSize: 10 }} tickFormatter={d => d.slice(5)} />
                    <YAxis tick={{ fontSize: 10 }} allowDecimals={false} />
                    <Tooltip />
                    <Bar dataKey="emails" fill="#f97316" radius={[4,4,0,0]} />
                  </BarChart>
                </div>
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200">
                    <h3 className="font-black text-slate-900 uppercase tracking-wide text-sm">Per-User Breakdown</h3>
                  </div>
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">User</th>
                        <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Sent</th>
                        <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Replies</th>
                        <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Reply Rate</th>
                        <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Open Rate</th>
                      </tr></thead>
                      <tbody>{outreach.per_user.map((u, i) => (
                        <tr key={u.user_id} className={i % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <td className="px-4 py-3">
                            <p className="font-semibold text-slate-900">{u.name}</p>
                            <p className="text-xs text-slate-400">{u.email}</p>
                          </td>
                          <td className="px-4 py-3 text-right font-bold text-slate-700">{u.emails_sent}</td>
                          <td className="px-4 py-3 text-right text-slate-600">{u.replies_received}</td>
                          <td className="px-4 py-3 text-right">
                            <span className={`font-bold ${u.reply_rate >= 10 ? "text-green-600" : u.reply_rate > 0 ? "text-orange-500" : "text-slate-400"}`}>{u.reply_rate > 0 ? `${u.reply_rate}%` : "—"}</span>
                          </td>
                          <td className="px-4 py-3 text-right text-slate-400 text-xs">—</td>
                        </tr>
                      ))}</tbody>
                    </table>
                  </div>
                </div>
              </>)}
            </div>
          )}

          {/* ── Growth Tracker sub-tab ── */}
          {outreachSubTab === "growth" && (() => {
            const TIER_LABEL = { tier_1: "Tier 1", tier_2: "Tier 2", tier_3: "Tier 3" };
            const TIER_COLOR = { tier_1: "bg-red-100 text-red-700", tier_2: "bg-amber-100 text-amber-700", tier_3: "bg-slate-100 text-slate-600" };
            const STATUS_CFG = {
              not_drafted:  { label: "No Draft",    cls: "bg-slate-100 text-slate-500" },
              approved:     { label: "Approved",    cls: "bg-amber-100 text-amber-700" },
              sent:         { label: "Sent",        cls: "bg-green-100 text-green-700" },
              bounced:      { label: "Bounced",     cls: "bg-red-100 text-red-600" },
            };
            return (
              <div className="space-y-8">

                {/* ── Academy Targets ── */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                      <h3 className="font-black text-slate-900 uppercase tracking-wide text-sm">Academy Targets</h3>
                      <p className="text-xs text-slate-500 mt-0.5">{growthTargets.length} academies</p>
                    </div>
                    <button onClick={() => setShowNewTarget(v => !v)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-700 transition-colors"
                      data-testid="gt-new-target-btn">
                      <Plus size={13} /> New Target
                    </button>
                  </div>

                  {/* New target form */}
                  {showNewTarget && (
                    <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 space-y-3" data-testid="gt-new-target-form">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">New Academy Target</p>
                      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Academy Name *</label>
                          <input value={newTargetForm.name} onChange={e => setNewTargetForm(p => ({...p, name: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            placeholder="e.g. Myerscough College" data-testid="gt-new-name" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Region</label>
                          <input value={newTargetForm.region} onChange={e => setNewTargetForm(p => ({...p, region: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            placeholder="e.g. Preston, Lancashire" data-testid="gt-new-region" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Tier</label>
                          <select value={newTargetForm.tier} onChange={e => setNewTargetForm(p => ({...p, tier: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            data-testid="gt-new-tier">
                            <option value="tier_1">Tier 1</option>
                            <option value="tier_2">Tier 2</option>
                            <option value="tier_3">Tier 3</option>
                          </select>
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Conference</label>
                          <input value={newTargetForm.conference} onChange={e => setNewTargetForm(p => ({...p, conference: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            placeholder="e.g. North" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Contact Email</label>
                          <input value={newTargetForm.contact_email} onChange={e => setNewTargetForm(p => ({...p, contact_email: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            placeholder="coach@academy.ac.uk" data-testid="gt-new-email" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Contact Name</label>
                          <input value={newTargetForm.contact_name} onChange={e => setNewTargetForm(p => ({...p, contact_name: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            placeholder="Head Coach" />
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <button onClick={createTarget} disabled={creatingTarget}
                          className="px-4 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-bold hover:bg-orange-600 disabled:opacity-50 transition-colors"
                          data-testid="gt-create-target-btn">
                          {creatingTarget ? "Creating…" : "Create Target"}
                        </button>
                        <button onClick={() => setShowNewTarget(false)}
                          className="px-4 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-50">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Targets table */}
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="gt-targets-table">
                      <thead><tr className="bg-slate-50 border-b border-slate-200">
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Academy</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Tier</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Contact</th>
                        <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                        <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Actions</th>
                      </tr></thead>
                      <tbody>
                        {growthTargets.length === 0 && (
                          <tr><td colSpan={5} className="px-4 py-8 text-center text-slate-400 text-sm">No academy targets yet — click "New Target" to add one.</td></tr>
                        )}
                        {growthTargets.map((tgt, i) => {
                          const st = STATUS_CFG[tgt.send_status] || STATUS_CFG.not_drafted;
                          const isEditing = editingTarget?.id === tgt.id;
                          return (
                            <tr key={tgt.id} className={`border-b border-slate-100 ${isEditing ? "bg-orange-50" : i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}
                              data-testid={`gt-target-row-${tgt.id}`}>
                              <td className="px-4 py-3">
                                <p className="font-semibold text-slate-900">{tgt.name}</p>
                                {tgt.region && <p className="text-xs text-slate-400">{tgt.region}</p>}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${TIER_COLOR[tgt.tier] || "bg-slate-100 text-slate-500"}`}>
                                  {TIER_LABEL[tgt.tier] || tgt.tier}
                                </span>
                              </td>
                              <td className="px-4 py-3">
                                {tgt.contact_email
                                  ? <><p className="text-xs font-medium text-slate-700">{tgt.contact_name || "—"}</p><p className="text-xs text-slate-400">{tgt.contact_email}</p></>
                                  : <span className="text-xs text-slate-400 italic">No contact yet</span>}
                              </td>
                              <td className="px-4 py-3">
                                <span className={`text-xs font-bold px-2 py-0.5 rounded-full ${st.cls}`} data-testid={`gt-status-${tgt.id}`}>{st.label}</span>
                                {tgt.sent_at && <p className="text-[10px] text-slate-400 mt-0.5">Sent {new Date(tgt.sent_at).toLocaleDateString()}</p>}
                              </td>
                              <td className="px-4 py-3">
                                <div className="flex items-center justify-end gap-1.5">
                                  <button onClick={() => isEditing ? setEditingTarget(null) : openEditTarget(tgt)}
                                    className={`flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold border transition-colors ${isEditing ? "bg-slate-200 text-slate-700 border-slate-300" : "border-slate-300 text-slate-600 hover:bg-slate-50"}`}
                                    data-testid={`gt-edit-btn-${tgt.id}`}>
                                    <Edit2 size={11} /> {isEditing ? "Close" : "Edit"}
                                  </button>
                                  {tgt.send_status === "not_drafted" && (
                                    <button onClick={() => approveTarget(tgt.id)} disabled={approvingTarget === tgt.id}
                                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-amber-500 text-white hover:bg-amber-600 disabled:opacity-50 transition-colors"
                                      data-testid={`gt-approve-btn-${tgt.id}`}>
                                      <CheckCircle2 size={11} /> {approvingTarget === tgt.id ? "…" : "Approve"}
                                    </button>
                                  )}
                                  {tgt.send_status === "approved" && (
                                    <button onClick={() => { if (window.confirm(`Send email to ${tgt.contact_email}?`)) sendTargetEmail(tgt.id); }}
                                      disabled={sendingTarget === tgt.id}
                                      className="flex items-center gap-1 px-2.5 py-1 rounded-lg text-xs font-semibold bg-orange-500 text-white hover:bg-orange-600 disabled:opacity-50 transition-colors"
                                      data-testid={`gt-send-btn-${tgt.id}`}>
                                      <Send size={11} /> {sendingTarget === tgt.id ? "Sending…" : "Send"}
                                    </button>
                                  )}
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>

                  {/* Edit flyout */}
                  {editingTarget && (
                    <div className="border-t border-slate-200 bg-slate-50 px-6 py-5 space-y-4" data-testid="gt-edit-flyout">
                      <div className="flex items-center justify-between">
                        <p className="font-black text-slate-900 text-sm uppercase tracking-wide flex items-center gap-2">
                          <PenLine size={14} className="text-orange-500" /> Editing: {editingTarget.name}
                        </p>
                        <button onClick={() => setEditingTarget(null)} className="p-1 rounded-lg hover:bg-slate-200 text-slate-500" data-testid="gt-edit-close">
                          <X size={14} />
                        </button>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Contact Name</label>
                          <input value={targetForm.contact_name || ""} onChange={e => setTargetForm(p => ({...p, contact_name: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            placeholder="e.g. Head Coach" data-testid="gt-form-contact-name" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Contact Email</label>
                          <input value={targetForm.contact_email || ""} onChange={e => setTargetForm(p => ({...p, contact_email: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            placeholder="coach@academy.ac.uk" data-testid="gt-form-contact-email" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Contact Role</label>
                          <input value={targetForm.contact_role || ""} onChange={e => setTargetForm(p => ({...p, contact_role: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            placeholder="e.g. Head of Basketball" />
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-1">Email Subject</label>
                        <input value={targetForm.draft_email_subject || ""} onChange={e => setTargetForm(p => ({...p, draft_email_subject: e.target.value}))}
                          className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                          placeholder="Subject line…" data-testid="gt-form-subject" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-1">Email Body</label>
                        <textarea value={targetForm.draft_email_body || ""} onChange={e => setTargetForm(p => ({...p, draft_email_body: e.target.value}))}
                          rows={8}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y font-mono"
                          placeholder="Email body…" data-testid="gt-form-body" />
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-1">Internal Notes</label>
                        <input value={targetForm.notes || ""} onChange={e => setTargetForm(p => ({...p, notes: e.target.value}))}
                          className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                          placeholder="Notes visible only to admin" />
                      </div>
                      <div className="flex items-center gap-2">
                        <button onClick={saveTargetForm} disabled={targetFormSaving}
                          className="px-4 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-700 disabled:opacity-50 transition-colors"
                          data-testid="gt-save-btn">
                          {targetFormSaving ? "Saving…" : "Save Changes"}
                        </button>
                        {editingTarget.send_status === "not_drafted" && (
                          <button onClick={() => approveTarget(editingTarget.id)} disabled={approvingTarget === editingTarget.id}
                            className="px-4 py-1.5 bg-amber-500 text-white rounded-lg text-xs font-bold hover:bg-amber-600 disabled:opacity-50 transition-colors"
                            data-testid="gt-flyout-approve-btn">
                            <span className="flex items-center gap-1"><CheckCircle2 size={11} /> {approvingTarget === editingTarget.id ? "Approving…" : "Approve Draft"}</span>
                          </button>
                        )}
                        {editingTarget.send_status === "approved" && (
                          <button onClick={() => { if (window.confirm(`Send email to ${editingTarget.contact_email}?`)) sendTargetEmail(editingTarget.id); }}
                            disabled={sendingTarget === editingTarget.id}
                            className="px-4 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-bold hover:bg-orange-600 disabled:opacity-50 transition-colors"
                            data-testid="gt-flyout-send-btn">
                            <span className="flex items-center gap-1"><Send size={11} /> {sendingTarget === editingTarget.id ? "Sending…" : "Send Email"}</span>
                          </button>
                        )}
                        <span className="text-xs text-slate-400">Status: <strong>{STATUS_CFG[editingTarget.send_status]?.label || editingTarget.send_status}</strong></span>
                      </div>
                    </div>
                  )}
                </div>

                {/* ── Content Bank ── */}
                <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                  <div className="px-6 py-4 border-b border-slate-200 flex items-center justify-between">
                    <div>
                      <h3 className="font-black text-slate-900 uppercase tracking-wide text-sm flex items-center gap-2">
                        <FileText size={14} className="text-slate-500" /> Content Bank
                      </h3>
                      <p className="text-xs text-slate-500 mt-0.5">{growthContentBank.length} drafts</p>
                    </div>
                    <button onClick={() => setShowNewContent(v => !v)}
                      className="flex items-center gap-1.5 px-3 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-700 transition-colors"
                      data-testid="gt-new-content-btn">
                      <Plus size={13} /> New Draft
                    </button>
                  </div>

                  {/* New content form */}
                  {showNewContent && (
                    <div className="px-6 py-4 bg-slate-50 border-b border-slate-200 space-y-3" data-testid="gt-new-content-form">
                      <p className="text-xs font-bold uppercase tracking-wide text-slate-500">New Content Draft</p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Title *</label>
                          <input value={newContentForm.title} onChange={e => setNewContentForm(p => ({...p, title: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            placeholder="Draft title" data-testid="gt-new-content-title" />
                        </div>
                        <div>
                          <label className="text-xs font-semibold text-slate-600 block mb-1">Pillar</label>
                          <select value={newContentForm.pillar} onChange={e => setNewContentForm(p => ({...p, pillar: e.target.value}))}
                            className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400"
                            data-testid="gt-new-content-pillar">
                            <option value="">— Select pillar —</option>
                            <option value="success_story">Success Story</option>
                            <option value="coach_perspective">Coach Perspective</option>
                            <option value="platform_explainer">Platform Explainer</option>
                            <option value="parent_focused">Parent Focused</option>
                            <option value="myth_busting">Myth Busting</option>
                          </select>
                        </div>
                      </div>
                      <div>
                        <label className="text-xs font-semibold text-slate-600 block mb-1">Body *</label>
                        <textarea value={newContentForm.body} onChange={e => setNewContentForm(p => ({...p, body: e.target.value}))}
                          rows={5}
                          className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y font-mono"
                          placeholder="Content body…" data-testid="gt-new-content-body" />
                      </div>
                      <div className="flex gap-2">
                        <button onClick={createContentItem} disabled={creatingContent}
                          className="px-4 py-1.5 bg-orange-500 text-white rounded-lg text-xs font-bold hover:bg-orange-600 disabled:opacity-50 transition-colors"
                          data-testid="gt-create-content-btn">
                          {creatingContent ? "Saving…" : "Save Draft"}
                        </button>
                        <button onClick={() => setShowNewContent(false)}
                          className="px-4 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-50">
                          Cancel
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Content list */}
                  <div className="divide-y divide-slate-100">
                    {growthContentBank.length === 0 && (
                      <p className="px-6 py-8 text-center text-slate-400 text-sm">No content drafts yet.</p>
                    )}
                    {growthContentBank.map(item => (
                      <div key={item.id} className="px-6 py-4" data-testid={`gt-content-${item.id}`}>
                        {editingContent?.id === item.id ? (
                          <div className="space-y-3">
                            <div className="grid grid-cols-2 gap-3">
                              <div>
                                <label className="text-xs font-semibold text-slate-600 block mb-1">Title</label>
                                <input value={contentForm.title || ""} onChange={e => setContentForm(p => ({...p, title: e.target.value}))}
                                  className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400" />
                              </div>
                              <div>
                                <label className="text-xs font-semibold text-slate-600 block mb-1">Pillar</label>
                                <select value={contentForm.pillar || ""} onChange={e => setContentForm(p => ({...p, pillar: e.target.value}))}
                                  className="w-full border border-slate-300 rounded-lg px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400">
                                  <option value="">— Select pillar —</option>
                                  <option value="success_story">Success Story</option>
                                  <option value="coach_perspective">Coach Perspective</option>
                                  <option value="platform_explainer">Platform Explainer</option>
                                  <option value="parent_focused">Parent Focused</option>
                                  <option value="myth_busting">Myth Busting</option>
                                </select>
                              </div>
                            </div>
                            <textarea value={contentForm.body || ""} onChange={e => setContentForm(p => ({...p, body: e.target.value}))}
                              rows={5}
                              className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-400 resize-y font-mono" />
                            <div className="flex gap-2">
                              <button onClick={saveContentItem} disabled={contentFormSaving}
                                className="px-4 py-1.5 bg-slate-900 text-white rounded-lg text-xs font-bold hover:bg-slate-700 disabled:opacity-50">
                                {contentFormSaving ? "Saving…" : "Save"}
                              </button>
                              <button onClick={() => setEditingContent(null)}
                                className="px-4 py-1.5 border border-slate-300 text-slate-600 rounded-lg text-xs font-semibold hover:bg-slate-50">
                                Cancel
                              </button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-start justify-between gap-4">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2 mb-1">
                                {item.pillar && <span className="text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-500 px-1.5 py-0.5 rounded">{item.pillar.replace(/_/g, " ")}</span>}
                                <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded uppercase tracking-wide ${item.status === "published" ? "bg-green-100 text-green-700" : item.status === "template" ? "bg-purple-100 text-purple-700" : "bg-slate-100 text-slate-500"}`}>{item.status}</span>
                                {item.requires_real_data && <span className="text-[10px] font-bold bg-red-100 text-red-600 px-1.5 py-0.5 rounded uppercase">Needs real data</span>}
                              </div>
                              <p className="font-semibold text-slate-900 text-sm">{item.title}</p>
                              <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.body}</p>
                            </div>
                            <button onClick={() => { setEditingContent(item); setContentForm({ title: item.title, pillar: item.pillar || "", body: item.body, status: item.status }); }}
                              className="flex-shrink-0 p-1.5 border border-slate-200 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-50"
                              data-testid={`gt-edit-content-${item.id}`}>
                              <Edit2 size={12} />
                            </button>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>

              </div>
            );
          })()}
        </div>
      )}

      {/* ── ACTIVITY TAB ───────────────────────────────────────── */}
      {activeTab === "activity" && (
        <div className="space-y-6">
          {!activity ? (
            <div className="flex items-center justify-center h-40"><div className="w-8 h-8 border-4 border-orange-500 border-t-transparent rounded-full animate-spin" /></div>
          ) : (() => {
            const STATUS = { active: { label: "Active", dot: "bg-green-500", text: "text-green-700", bg: "bg-green-50" }, idle: { label: "Idle", dot: "bg-yellow-400", text: "text-yellow-700", bg: "bg-yellow-50" }, dormant: { label: "Dormant", dot: "bg-red-400", text: "text-red-700", bg: "bg-red-50" } };
            const filters = [
              { id: "all", label: "All" },
              { id: "active", label: "Active (7d)" },
              { id: "idle", label: "Idle (7–30d)" },
              { id: "dormant", label: "Dormant (30d+)" },
              { id: "never_emailed", label: "Never Emailed" },
            ];
            const filtered = activity.users.filter(u => {
              if (activityFilter === "all") return true;
              if (activityFilter === "never_emailed") return u.emails_sent === 0;
              return u.status === activityFilter;
            });
            const counts = { active: activity.users.filter(u => u.status === "active").length, idle: activity.users.filter(u => u.status === "idle").length, dormant: activity.users.filter(u => u.status === "dormant").length, never_emailed: activity.users.filter(u => u.emails_sent === 0).length };
            const toggleSelect = (uid) => { const s = new Set(selectedUsers); s.has(uid) ? s.delete(uid) : s.add(uid); setSelectedUsers(s); };
            const selectAll = () => setSelectedUsers(new Set(filtered.map(u => u.user_id)));
            return (<>
              {/* Summary Cards */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {[
                  { label: "Active (7d)", count: counts.active, color: "text-green-600", bg: "bg-green-50 border-green-200" },
                  { label: "Idle (7–30d)", count: counts.idle, color: "text-yellow-600", bg: "bg-yellow-50 border-yellow-200" },
                  { label: "Dormant (30d+)", count: counts.dormant, color: "text-red-600", bg: "bg-red-50 border-red-200" },
                  { label: "Never Emailed", count: counts.never_emailed, color: "text-slate-500", bg: "bg-slate-50 border-slate-200" },
                ].map(c => (
                  <div key={c.label} className={`border rounded-xl p-5 ${c.bg}`}>
                    <p className="text-xs font-bold uppercase tracking-wider text-slate-500 mb-1">{c.label}</p>
                    <p className={`text-3xl font-black ${c.color}`}>{c.count}</p>
                  </div>
                ))}
              </div>

              {/* Filter + Bulk Nudge */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex flex-wrap gap-2">
                  {filters.map(f => (
                    <button key={f.id} onClick={() => { setActivityFilter(f.id); setSelectedUsers(new Set()); }}
                      className={`px-3 py-1.5 rounded-lg text-xs font-bold uppercase tracking-wide transition-all ${activityFilter === f.id ? "bg-slate-900 text-white" : "bg-white border border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                      {f.label} {counts[f.id] !== undefined ? `(${counts[f.id]})` : `(${activity.users.length})`}
                    </button>
                  ))}
                </div>
                <div className="flex gap-2">
                  {selectedUsers.size > 0 && (
                    <button onClick={() => sendNudge([...selectedUsers])} disabled={nudging}
                      className="flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold uppercase tracking-wider px-4 py-2 rounded-lg transition-colors disabled:opacity-60">
                      <Bell className="w-4 h-4" />
                      {nudging ? "Sending..." : `Nudge ${selectedUsers.size} user${selectedUsers.size !== 1 ? "s" : ""}`}
                    </button>
                  )}
                  <button onClick={selectAll} className="text-xs font-bold text-slate-500 hover:text-slate-900 px-3 py-2 border border-slate-200 rounded-lg bg-white">Select All</button>
                </div>
              </div>

              {nudgeResult && (
                <div className="bg-green-50 border border-green-200 rounded-lg px-4 py-3 text-sm font-semibold text-green-700 flex items-center gap-2">
                  <CheckCircle2 className="w-4 h-4" /> {nudgeResult}
                </div>
              )}

              {/* User Table */}
              <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
                <table className="w-full text-sm">
                  <thead><tr className="bg-slate-50 border-b border-slate-200">
                    <th className="px-4 py-3 w-8"><input type="checkbox" onChange={e => e.target.checked ? selectAll() : setSelectedUsers(new Set())} checked={selectedUsers.size === filtered.length && filtered.length > 0} className="rounded" /></th>
                    <th className="text-left px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">User</th>
                    <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Status</th>
                    <th className="text-center px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Tier</th>
                    <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Emails Sent</th>
                    <th className="text-right px-4 py-3 text-xs font-bold uppercase tracking-wider text-slate-500">Last Active</th>
                    <th className="px-4 py-3"></th>
                  </tr></thead>
                  <tbody>{filtered.map((u, i) => {
                    const s = STATUS[u.status] || STATUS.dormant;
                    return (
                      <tr key={u.user_id} className={`border-b border-slate-100 ${selectedUsers.has(u.user_id) ? "bg-orange-50" : i % 2 === 0 ? "bg-white" : "bg-slate-50"}`}>
                        <td className="px-4 py-3"><input type="checkbox" checked={selectedUsers.has(u.user_id)} onChange={() => toggleSelect(u.user_id)} className="rounded" /></td>
                        <td className="px-4 py-3">
                          <p className="font-semibold text-slate-900">{u.name || "—"}</p>
                          <p className="text-xs text-slate-400">{u.email}</p>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-bold ${s.bg} ${s.text}`}>
                            <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />{s.label}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center"><span className="text-xs font-bold uppercase tracking-wider text-slate-600">{u.subscription_tier}</span></td>
                        <td className="px-4 py-3 text-right">
                          {u.emails_sent === 0 ? <span className="text-xs text-red-400 font-semibold">None sent</span> : <span className="font-bold text-slate-700">{u.emails_sent}</span>}
                        </td>
                        <td className="px-4 py-3 text-right text-xs text-slate-400">{u.last_active ? new Date(u.last_active).toLocaleDateString() : "Never"}</td>
                        <td className="px-4 py-3 text-right">
                          <button onClick={() => sendNudge([u.user_id])} disabled={nudging}
                            className="text-xs font-bold text-orange-500 hover:text-orange-700 flex items-center gap-1 ml-auto">
                            <Bell className="w-3 h-3" /> Nudge
                          </button>
                        </td>
                      </tr>
                    );
                  })}</tbody>
                </table>
                {filtered.length === 0 && <div className="text-center py-12 text-slate-400 text-sm">No users match this filter</div>}
              </div>
            </>);
          })()}
        </div>
      )}

      {/* ── EDIT COLLEGE MODAL ───────────────────────────────── */}
      {editCollege && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-slate-200 px-6 py-4 flex items-center justify-between rounded-t-2xl">
              <h3 className="font-black text-slate-900 text-lg uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>
                Edit College
              </h3>
              <button onClick={() => setEditCollege(null)} className="text-slate-400 hover:text-slate-700"><XCircle className="w-5 h-5" /></button>
            </div>
            <div className="p-6 space-y-4">
              {/* Basic fields */}
              <div className="grid grid-cols-2 gap-4">
                {[
                  { key: "name", label: "College Name", full: true },
                  { key: "location", label: "Location (City, State)" },
                  { key: "state", label: "State" },
                  { key: "division", label: "Division" },
                  { key: "conference", label: "Conference" },
                  { key: "region", label: "Region" },
                  { key: "acceptance_rate", label: "Acceptance Rate" },
                  { key: "ranking", label: "Ranking #" },
                  { key: "website", label: "Website URL" },
                  { key: "image_url", label: "Image URL" },
                ].map(f => (
                  <div key={f.key} className={f.full ? "col-span-2" : ""}>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">{f.label}</label>
                    <input type="text" value={editForm[f.key] ?? ""} onChange={e => setEditForm(p => ({ ...p, [f.key]: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" />
                  </div>
                ))}
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Notable Alumni</label>
                  <input type="text" value={editForm.notable_alumni ?? ""} onChange={e => setEditForm(p => ({ ...p, notable_alumni: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none" />
                </div>
                <div className="col-span-2">
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Scholarship Info</label>
                  <textarea rows={2} value={editForm.scholarship_info ?? ""} onChange={e => setEditForm(p => ({ ...p, scholarship_info: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none resize-none" />
                </div>
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">UK Friendly</label>
                  <select value={editForm.foreign_friendly} onChange={e => setEditForm(p => ({ ...p, foreign_friendly: e.target.value }))}
                    className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-orange-400 outline-none bg-white">
                    <option value="yes">Yes</option>
                    <option value="no">No</option>
                  </select>
                </div>
              </div>

              {/* ── Late Roster Opening — Men's ──────────────────────────── */}
              <div className="border border-blue-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border-b border-blue-200">
                  <span className="text-xs font-black text-blue-700 uppercase tracking-wider">Men's Late Roster Opening</span>
                  <button
                    data-testid="save-late-opening-mens-btn"
                    onClick={() => saveLateOpeningForGender("mens")}
                    disabled={editSavingLateOpeningMens}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-colors disabled:opacity-60"
                  >
                    {editSavingLateOpeningMens ? "Saving..." : editSavedLateOpeningMens ? "Saved!" : "Save Men's"}
                  </button>
                </div>
                <div className="p-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Status</label>
                    <select
                      data-testid="late-opening-mens-status-select"
                      value={lateOpeningMens.status}
                      onChange={e => setLateOpeningMens(p => ({ ...p, status: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 outline-none bg-white"
                    >
                      <option value="unconfirmed">Unconfirmed</option>
                      <option value="likely_open">Likely Open</option>
                      <option value="confirmed_open">Confirmed Open</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div className={!["likely_open", "confirmed_open"].includes(lateOpeningMens.status) ? "opacity-40 pointer-events-none" : ""}>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Confidence</label>
                    <select
                      data-testid="late-opening-mens-confidence-select"
                      value={lateOpeningMens.confidence}
                      onChange={e => setLateOpeningMens(p => ({ ...p, confidence: e.target.value || null }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 outline-none bg-white"
                    >
                      <option value="">— not set —</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Source</label>
                    <input
                      type="text"
                      data-testid="late-opening-mens-source-input"
                      placeholder="e.g. Coach confirmed via email, Roster count observation"
                      value={lateOpeningMens.source}
                      onChange={e => setLateOpeningMens(p => ({ ...p, source: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Notes</label>
                    <textarea
                      rows={2}
                      data-testid="late-opening-mens-notes-textarea"
                      placeholder="Free text notes about this opening"
                      value={lateOpeningMens.notes}
                      onChange={e => setLateOpeningMens(p => ({ ...p, notes: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-400 outline-none resize-none"
                    />
                  </div>
                  {lateOpeningMensVerified?.verified_at && (
                    <div className="col-span-2 text-xs text-slate-400">
                      Last saved by{" "}
                      <span className="font-semibold text-slate-500">{lateOpeningMensVerified.verified_by || "admin"}</span>
                      {" "}on {new Date(lateOpeningMensVerified.verified_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>

              {/* ── Late Roster Opening — Women's ─────────────────────────── */}
              <div className="border border-purple-200 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-purple-50 border-b border-purple-200">
                  <span className="text-xs font-black text-purple-700 uppercase tracking-wider">Women's Late Roster Opening</span>
                  <button
                    data-testid="save-late-opening-womens-btn"
                    onClick={() => saveLateOpeningForGender("womens")}
                    disabled={editSavingLateOpeningWomens}
                    className="px-3 py-1.5 bg-purple-600 hover:bg-purple-700 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-colors disabled:opacity-60"
                  >
                    {editSavingLateOpeningWomens ? "Saving..." : editSavedLateOpeningWomens ? "Saved!" : "Save Women's"}
                  </button>
                </div>
                <div className="p-4 grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Status</label>
                    <select
                      data-testid="late-opening-womens-status-select"
                      value={lateOpeningWomens.status}
                      onChange={e => setLateOpeningWomens(p => ({ ...p, status: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 outline-none bg-white"
                    >
                      <option value="unconfirmed">Unconfirmed</option>
                      <option value="likely_open">Likely Open</option>
                      <option value="confirmed_open">Confirmed Open</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>
                  <div className={!["likely_open", "confirmed_open"].includes(lateOpeningWomens.status) ? "opacity-40 pointer-events-none" : ""}>
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Confidence</label>
                    <select
                      data-testid="late-opening-womens-confidence-select"
                      value={lateOpeningWomens.confidence}
                      onChange={e => setLateOpeningWomens(p => ({ ...p, confidence: e.target.value || null }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 outline-none bg-white"
                    >
                      <option value="">— not set —</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Source</label>
                    <input
                      type="text"
                      data-testid="late-opening-womens-source-input"
                      placeholder="e.g. Coach confirmed via email, Roster count observation"
                      value={lateOpeningWomens.source}
                      onChange={e => setLateOpeningWomens(p => ({ ...p, source: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 outline-none"
                    />
                  </div>
                  <div className="col-span-2">
                    <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Notes</label>
                    <textarea
                      rows={2}
                      data-testid="late-opening-womens-notes-textarea"
                      placeholder="Free text notes about this opening"
                      value={lateOpeningWomens.notes}
                      onChange={e => setLateOpeningWomens(p => ({ ...p, notes: e.target.value }))}
                      className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-purple-400 outline-none resize-none"
                    />
                  </div>
                  {lateOpeningWomensVerified?.verified_at && (
                    <div className="col-span-2 text-xs text-slate-400">
                      Last saved by{" "}
                      <span className="font-semibold text-slate-500">{lateOpeningWomensVerified.verified_by || "admin"}</span>
                      {" "}on {new Date(lateOpeningWomensVerified.verified_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              </div>

              {/* Image preview */}
              {editForm.image_url && (
                <div>
                  <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1">Image Preview</label>
                  <img src={editForm.image_url} alt="preview" className="h-24 rounded-lg object-cover border border-slate-200" onError={e => e.target.style.display="none"} />
                </div>
              )}

              {/* Logo upload */}
              <div>
                <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">Upload Logo / Image</label>
                <p className="text-xs text-slate-400 mb-2">Upload PNG or JPG from the college website. This replaces the placeholder image.</p>
                <label className="flex items-center gap-2 w-fit px-4 py-2 bg-orange-500 hover:bg-orange-600 text-white text-xs font-bold uppercase tracking-wide rounded-lg cursor-pointer transition-all">
                  <Upload className="w-3.5 h-3.5" />
                  Choose Image File
                  <input type="file" accept="image/png,image/jpeg,image/jpg,image/webp" className="hidden"
                    onChange={async (e) => {
                      const file = e.target.files?.[0];
                      if (!file || !editCollege) return;
                      const formData = new FormData();
                      formData.append("file", file);
                      try {
                        const token = localStorage.getItem("cb_admin_token");
                        const API = process.env.REACT_APP_BACKEND_URL;
                        const res = await fetch(`${API}/api/admin/colleges/${editCollege.id}/upload-image`, {
                          method: "POST",
                          headers: { Authorization: `Bearer ${token}` },
                          body: formData,
                        });
                        const data = await res.json().catch(() => null);
                        if (!res.ok || !data?.image_url) {
                          const msg = data?.detail || `Upload failed (${res.status})`;
                          console.error("Image upload failed:", msg);
                          toast.error(msg);
                        } else {
                          setEditForm(p => ({ ...p, image_url: data.image_url }));
                          toast.success("Image uploaded.");
                        }
                      } catch (err) {
                        console.error("Image upload failed:", err);
                        toast.error("Upload failed — check your connection and try again.");
                      }
                      e.target.value = "";
                    }}
                  />
                </label>
                {editForm.image_url?.startsWith("https://") && editForm.image_url.includes("supabase") && (
                  <p className="text-xs text-green-600 font-semibold mt-1">Custom logo uploaded</p>
                )}
              </div>

              {/* ── Men's Coaches ─────────────────────────────── */}
              <div className="border border-blue-100 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-blue-50 border-b border-blue-100">
                  <span className="text-xs font-black text-blue-700 uppercase tracking-wider">Men's Coaches</span>
                  <button
                    data-testid="save-mens-coaches-btn"
                    onClick={() => saveCoachSection("mens")}
                    disabled={editSavingMens}
                    className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-colors disabled:opacity-60"
                  >
                    {editSavingMens ? "Saving..." : editSavedMens ? "Saved!" : "Save Men's Coaches"}
                  </button>
                </div>
                <div className="p-3 space-y-2">
                  {(editForm.mensCoaches || []).map((coach, ci) => (
                    <div key={ci} className="grid grid-cols-2 gap-2 p-3 border border-slate-100 rounded-lg bg-slate-50">
                      <input placeholder="Name" value={coach.name || ""} onChange={e => { const c=[...editForm.mensCoaches]; c[ci]={...c[ci],name:e.target.value}; setEditForm(p=>({...p,mensCoaches:c})); }}
                        className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-400 outline-none" />
                      <input placeholder="Title" value={coach.title || ""} onChange={e => { const c=[...editForm.mensCoaches]; c[ci]={...c[ci],title:e.target.value}; setEditForm(p=>({...p,mensCoaches:c})); }}
                        className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-400 outline-none" />
                      <input placeholder="Email" value={coach.email || ""} onChange={e => { const c=[...editForm.mensCoaches]; c[ci]={...c[ci],email:e.target.value}; setEditForm(p=>({...p,mensCoaches:c})); }}
                        className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-400 outline-none" />
                      <input placeholder="Phone" value={coach.phone || ""} onChange={e => { const c=[...editForm.mensCoaches]; c[ci]={...c[ci],phone:e.target.value}; setEditForm(p=>({...p,mensCoaches:c})); }}
                        className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-400 outline-none" />
                      <div className="col-span-2 flex items-center gap-2">
                        <label className="text-xs text-slate-500 font-semibold w-28 flex-shrink-0">Last Verified:</label>
                        <input type="date" value={coach.last_verified || ""} onChange={e => { const c=[...editForm.mensCoaches]; c[ci]={...c[ci],last_verified:e.target.value}; setEditForm(p=>({...p,mensCoaches:c})); }}
                          className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-blue-400 outline-none flex-1" />
                        <button type="button" onClick={() => { const c=[...editForm.mensCoaches]; c[ci]={...c[ci],last_verified:new Date().toISOString().slice(0,10)}; setEditForm(p=>({...p,mensCoaches:c})); }}
                          className="text-xs text-green-600 font-semibold hover:text-green-800 whitespace-nowrap">Mark Today</button>
                      </div>
                      <button onClick={() => setEditForm(p => ({ ...p, mensCoaches: p.mensCoaches.filter((_,ii) => ii!==ci) }))}
                        className="col-span-2 text-xs text-red-500 hover:text-red-700 text-left font-semibold">Remove coach</button>
                    </div>
                  ))}
                  <button onClick={() => setEditForm(p => ({ ...p, mensCoaches: [...(p.mensCoaches||[]), {name:"",title:"Head Coach",email:"",phone:""}] }))}
                    className="text-xs text-blue-600 hover:text-blue-800 font-bold">+ Add Men's Coach</button>
                </div>
              </div>

              {/* ── Women's Coaches ────────────────────────────── */}
              <div className="border border-pink-100 rounded-xl overflow-hidden">
                <div className="flex items-center justify-between px-4 py-2.5 bg-pink-50 border-b border-pink-100">
                  <span className="text-xs font-black text-pink-700 uppercase tracking-wider">Women's Coaches</span>
                  <button
                    data-testid="save-womens-coaches-btn"
                    onClick={() => saveCoachSection("womens")}
                    disabled={editSavingWomens}
                    className="px-3 py-1.5 bg-pink-600 hover:bg-pink-700 text-white text-xs font-bold uppercase tracking-wide rounded-lg transition-colors disabled:opacity-60"
                  >
                    {editSavingWomens ? "Saving..." : editSavedWomens ? "Saved!" : "Save Women's Coaches"}
                  </button>
                </div>
                <div className="p-3 space-y-2">
                  {(editForm.womensCoaches || []).map((coach, ci) => (
                    <div key={ci} className="grid grid-cols-2 gap-2 p-3 border border-slate-100 rounded-lg bg-slate-50">
                      <input placeholder="Name" value={coach.name || ""} onChange={e => { const c=[...editForm.womensCoaches]; c[ci]={...c[ci],name:e.target.value}; setEditForm(p=>({...p,womensCoaches:c})); }}
                        className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-pink-400 outline-none" />
                      <input placeholder="Title" value={coach.title || ""} onChange={e => { const c=[...editForm.womensCoaches]; c[ci]={...c[ci],title:e.target.value}; setEditForm(p=>({...p,womensCoaches:c})); }}
                        className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-pink-400 outline-none" />
                      <input placeholder="Email" value={coach.email || ""} onChange={e => { const c=[...editForm.womensCoaches]; c[ci]={...c[ci],email:e.target.value}; setEditForm(p=>({...p,womensCoaches:c})); }}
                        className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-pink-400 outline-none" />
                      <input placeholder="Phone" value={coach.phone || ""} onChange={e => { const c=[...editForm.womensCoaches]; c[ci]={...c[ci],phone:e.target.value}; setEditForm(p=>({...p,womensCoaches:c})); }}
                        className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-pink-400 outline-none" />
                      <div className="col-span-2 flex items-center gap-2">
                        <label className="text-xs text-slate-500 font-semibold w-28 flex-shrink-0">Last Verified:</label>
                        <input type="date" value={coach.last_verified || ""} onChange={e => { const c=[...editForm.womensCoaches]; c[ci]={...c[ci],last_verified:e.target.value}; setEditForm(p=>({...p,womensCoaches:c})); }}
                          className="border border-slate-200 rounded px-2 py-1.5 text-xs focus:ring-1 focus:ring-pink-400 outline-none flex-1" />
                        <button type="button" onClick={() => { const c=[...editForm.womensCoaches]; c[ci]={...c[ci],last_verified:new Date().toISOString().slice(0,10)}; setEditForm(p=>({...p,womensCoaches:c})); }}
                          className="text-xs text-green-600 font-semibold hover:text-green-800 whitespace-nowrap">Mark Today</button>
                      </div>
                      <button onClick={() => setEditForm(p => ({ ...p, womensCoaches: p.womensCoaches.filter((_,ii) => ii!==ci) }))}
                        className="col-span-2 text-xs text-red-500 hover:text-red-700 text-left font-semibold">Remove coach</button>
                    </div>
                  ))}
                  <button onClick={() => setEditForm(p => ({ ...p, womensCoaches: [...(p.womensCoaches||[]), {name:"",title:"Head Coach",email:"",phone:""}] }))}
                    className="text-xs text-pink-600 hover:text-pink-800 font-bold">+ Add Women's Coach</button>
                </div>
              </div>

              {/* ── Unassigned Coaches (should be empty after data fix) ── */}
              {(editForm.unassignedCoaches || []).length > 0 && (
                <div className="border border-yellow-200 rounded-xl overflow-hidden">
                  <div className="px-4 py-2.5 bg-yellow-50 border-b border-yellow-200">
                    <span className="text-xs font-black text-yellow-700 uppercase tracking-wider">Unassigned Coaches</span>
                    <p className="text-xs text-yellow-600 mt-0.5">These coaches have no programme gender set. Edit them via the Import Queue or use the data audit script.</p>
                  </div>
                  <div className="p-3 space-y-1">
                    {(editForm.unassignedCoaches || []).map((coach, ci) => (
                      <div key={ci} className="text-xs text-slate-600 px-2 py-1 bg-yellow-50 rounded">
                        {coach.name || "(unnamed)"} — {coach.title || ""}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="flex gap-3 pt-2">
                <button data-testid="save-college-btn" onClick={saveEditCollege} disabled={editSaving}
                  className="flex-1 bg-orange-500 hover:bg-orange-600 text-white font-black text-sm uppercase tracking-wider rounded-xl py-3 transition-all disabled:opacity-60">
                  {editSaving ? "Saving..." : editSaved ? "Saved!" : "Save Changes"}
                </button>
                <button onClick={() => setEditCollege(null)}
                  className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm uppercase tracking-wider rounded-xl py-3 transition-all">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE COACH CONFIRM MODAL ───────────────────────── */}
      {confirmDeleteCoach && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-black text-slate-900 text-lg uppercase" style={{ fontFamily: "Barlow Condensed, sans-serif" }}>Delete Coach</h3>
                <p className="text-xs text-slate-500">This removes the coach from the college</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
              <p className="text-sm font-bold text-red-800">{confirmDeleteCoach.coach_name}</p>
              <p className="text-xs text-red-600">{confirmDeleteCoach.college_name}</p>
              {confirmDeleteCoach.programme_gender && (
                <p className="text-xs text-red-500 mt-0.5 font-semibold">
                  {confirmDeleteCoach.programme_gender === "womens" ? "Women's programme" : "Men's programme"}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              <button data-testid="confirm-delete-coach-btn" onClick={deleteCoach} disabled={deletingCoach}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold text-sm uppercase tracking-wider rounded-xl py-3 transition-colors disabled:opacity-60">
                {deletingCoach ? "Deleting..." : "Delete Coach"}
              </button>
              <button onClick={() => setConfirmDeleteCoach(null)}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm uppercase tracking-wider rounded-xl py-3">
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── DELETE CONFIRM MODAL ──────────────────────────────── */}
      {confirmDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-red-100 flex items-center justify-center flex-shrink-0">
                <Trash2 className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <h3 className="font-black text-slate-900 text-lg" style={{ fontFamily: "Barlow Condensed, sans-serif", textTransform: "uppercase" }}>
                  Delete User Account
                </h3>
                <p className="text-xs text-slate-500">This action cannot be undone</p>
              </div>
            </div>
            <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 mb-5">
              <p className="text-sm font-bold text-red-800">{confirmDelete.name || "Unknown"}</p>
              <p className="text-xs text-red-600">{confirmDelete.email}</p>
              <p className="text-xs text-red-600 mt-2">
                All their data will be permanently deleted — tracked colleges, sent emails, profile, goals, and templates.
              </p>
            </div>
            <div className="flex gap-3">
              <button
                data-testid="confirm-delete-user-btn"
                onClick={deleteUser}
                disabled={deleting}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white font-bold text-sm uppercase tracking-wider rounded-xl py-3 transition-colors disabled:opacity-60"
              >
                {deleting ? "Deleting..." : "Yes, Delete Account"}
              </button>
              <button
                data-testid="cancel-delete-user-btn"
                onClick={() => setConfirmDelete(null)}
                className="flex-1 bg-slate-100 hover:bg-slate-200 text-slate-700 font-bold text-sm uppercase tracking-wider rounded-xl py-3 transition-colors"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
